import {
  ref as dbRef, push, set, update, remove, get, onValue,
  query, orderByChild, limitToLast, startAt, endAt, runTransaction,
} from 'firebase/database';
import { db, auth } from '../firebase';
import { compressImage, IMAGE_PRESETS, userFacingError } from './imageCompressionUtil';
import { roundMoney } from './currencyUtil';
import { isStoreAdminRole } from './roleUtil';
import { parsePosCardDimension } from './posCardSizeUtil';
import {
  normalisePosCustomizations, posProductCustomizations, normalisePosExtras, posProductExtras, posOrderItemLabel,
} from './posCustomizationUtil';
import {
  POS_TENANTS_NODE,
  POS_CATEGORIES_NODE,
  POS_CATEGORY_ICONS_NODE,
  POS_CATEGORY_ICON_MAX_LEN,
  POS_ICONIFY_SEARCH_URL,
  POS_ICONIFY_BASE_URL,
  POS_ICONIFY_SEARCH_LIMIT,
  POS_PRODUCTS_NODE,
  POS_ORDERS_NODE,
  POS_AUDIT_NODE,
  POS_CONFIG_NODE,
  POS_THEME_KEYS,
  POS_AUDIT_ACTIONS,
  POS_AUDIT_LABELS,
  POS_RECENT_ORDERS_LIMIT,
  POS_SEARCH_MIN_CHARS,
  POS_LOW_STOCK_DEFAULT,
  POS_VAT_MODE_NONE,
  POS_VAT_MODE_INCLUSIVE,
  POS_VAT_MODE_KEYS,
  POS_VAT_RATE_MAX,
  POS_VAT_INCLUSIVE_METHOD,
  POS_PAYMENT_METHODS,
  POS_DEFAULT_PAYMENT_METHOD,
  POS_CASH_METHOD,
  POS_DELIVERY_FEE_MAX,
  POS_TREND_MAX_BUCKETS,
  POS_HOURLY_GRANULARITY,
  POS_SORT_DESC,
  POS_SORT_BY_CUSTOM,
  POS_PRODUCT_SORT_KEYS,
  POS_PRODUCT_SORT_DEFAULT,
  POS_PRODUCT_PAGE_SIZE_KEY,
  POS_PRODUCT_PAGE_SIZE_MIN,
  POS_PRODUCT_PAGE_SIZE_MAX,
  POS_PRODUCT_PAGE_SIZE_DEFAULT,
  POS_CARD_WIDTH_KEY,
  POS_CARD_HEIGHT_KEY,
  POS_CARD_WIDTH_MIN,
  POS_CARD_WIDTH_MAX,
  POS_CARD_HEIGHT_MIN,
  POS_CARD_HEIGHT_MAX,
  POS_CARD_NAME_FONT_KEY,
  POS_CARD_PRICE_FONT_KEY,
  POS_CARD_DETAIL_FONT_KEY,
  POS_CARD_FONT_MIN,
  POS_CARD_FONT_MAX,
  POS_PACK_SIZE_KEY,
  POS_PACK_SIZE_MAX,
  POS_RESERVE_UNITS_MAX,
  POS_UNIT_LABEL_DEFAULT,
  POS_PACK_LABEL_DEFAULT,
  POS_STOCK_LABEL_MAX_LENGTH,
  POS_PAYMENT_METHODS_NODE,
  POS_PAYMENT_LABEL_MAX_LENGTH,
  POS_PAYMENT_METHODS_MAX,
  POS_BRANDING_NODE,
  POS_FONT_CHOICES,
  POS_FONT_KEYS,
  POS_FONT_DEFAULT,
  POS_CUSTOM_FONT_PREFIX,
  POS_CUSTOM_FONT_FAMILY_PREFIX,
  POS_RESERVE_ENABLED_KEY,
  POS_ROLE_OVERRIDES_NODE,
  POS_PERMISSION_KEYS,
  POS_CUSTOMIZATIONS_KEY,
  POS_EXTRAS_KEY,
  POS_COST_PRICE_KEY,
  POS_ORDER_DELETION_KEY,
  POS_SALE_STATUS_COMPLETED,
  POS_SALE_STATUS_DELETED,
  POS_DELETE_REASON_MIN_LENGTH,
  POS_DELETE_REASON_MAX_LENGTH,
} from '../constants/posConst';

// Everything the POS screens do to Firebase lives here: categories, products,
// orders and the module's own audit trail. The components never touch the
// database directly, which is what keeps the stock arithmetic — the one part
// that must be exactly right — in a single place.

/**
 * Who is acting, for the audit trail.
 *
 * The caller passes it, because the till deliberately does NOT hold a Firebase
 * session of its own: staff are verified on a separate auth instance and the
 * verified address is kept in the screen's own state, so that one browser can
 * run several clients and the admin screen at once. `auth.currentUser` in that
 * arrangement is the shared account — true, and useless on a receipt.
 *
 * Falls back to the signed-in account and then to 'Unknown' rather than
 * throwing: an audit entry naming the wrong person is bad, but a sale that
 * fails to record because nobody passed a name is worse.
 *
 * @param {string} [actor] - the staff address the screen verified.
 * @returns {string}
 */
const resolveActor = (actor) => String(actor || '').trim() || auth?.currentUser?.email || 'Unknown';

/**
 * A reference inside ONE client's subtree.
 *
 * Every read and write in this module goes through here, which is what makes
 * the tenant boundary a property of the module rather than of each caller
 * remembering to prefix a path. A missing clientId throws rather than
 * defaulting to anything: silently falling back to a shared location is how
 * one club's stock ends up on another club's till.
 *
 * @param {string} clientId - the client whose data to address.
 * @param {string} [suffix] - node under that client, e.g. 'products/abc123'.
 * @returns {import('firebase/database').DatabaseReference}
 */
export const tenantRef = (clientId, suffix = '') => {
  const id = String(clientId || '').trim();
  if (!id) throw new Error('posUtil: clientId is required — refusing to read or write an unscoped POS path.');
  return dbRef(db, `${POS_TENANTS_NODE}/${id}${suffix ? `/${suffix}` : ''}`);
};

/**
 * Appends one entry to the POS audit trail.
 *
 * Fire-and-forget by design: a sale that succeeded must not be reported as
 * failed because its audit write did. The entry is still awaited by nothing,
 * but failures are logged so a silently broken trail is noticeable.
 *
 * @param {{clientId: string, action: string, entityId?: string, summary?: object}} entry
 * @returns {void}
 */
export const logPosEvent = ({ clientId, action, entityId = null, summary = null, actor = '' }) => {
  push(tenantRef(clientId, POS_AUDIT_NODE), {
    action,
    entityId,
    summary: summary || null,
    performedBy: resolveActor(actor),
    timestamp: Date.now(),
  }).catch((error) => console.error('POS audit write failed:', error));
};

// ---- Categories -----------------------------------------------------------

/**
 * Live list of category names.
 *
 * Stored as an ARRAY of strings under one key rather than a node of records:
 * the brief calls for "just a list of strings", the whole list is read
 * together everywhere it is used, and ordering is the admin's.
 *
 * @param {string} clientId
 * @param {(categories: string[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosCategories = (clientId, callback) => onValue(
  tenantRef(clientId, POS_CATEGORIES_NODE),
  (snapshot) => {
    const value = snapshot.val();
    callback(Array.isArray(value) ? value.filter(Boolean) : []);
  },
  (error) => { console.error('POS categories listener failed:', error); callback([]); },
);

/**
 * Adds a category, refusing duplicates case-insensitively.
 *
 * Case-insensitive because "Drinks" and "drinks" would otherwise become two
 * tabs holding different products, which no one would notice until a product
 * went missing from the tab they expected.
 *
 * @param {string} name
 * @returns {Promise<string[]>} the saved list
 */
export const addPosCategory = async (clientId, name, actor = '') => {
  const clean = String(name || '').trim();
  if (!clean) throw userFacingError('Enter a category name.');

  const snapshot = await get(tenantRef(clientId, POS_CATEGORIES_NODE));
  const current = Array.isArray(snapshot.val()) ? snapshot.val().filter(Boolean) : [];
  if (current.some(c => c.toLowerCase() === clean.toLowerCase())) {
    throw userFacingError(`"${clean}" is already a category.`);
  }

  const next = [...current, clean];
  await set(tenantRef(clientId, POS_CATEGORIES_NODE), next);
  logPosEvent({ clientId, actor, action: POS_AUDIT_ACTIONS.CATEGORY_ADDED, entityId: clean, summary: { name: clean } });
  return next;
};

/**
 * Removes a category.
 *
 * Products keep the category string they were saved with — they are NOT
 * reassigned or deleted. A product whose category no longer exists still
 * appears under "All", so removing a category can never lose stock; it just
 * stops offering that tab.
 *
 * @param {string} name
 * @returns {Promise<string[]>} the saved list
 */
export const removePosCategory = async (clientId, name, actor = '') => {
  const snapshot = await get(tenantRef(clientId, POS_CATEGORIES_NODE));
  const current = Array.isArray(snapshot.val()) ? snapshot.val().filter(Boolean) : [];
  const next = current.filter(c => c !== name);
  await set(tenantRef(clientId, POS_CATEGORIES_NODE), next);
  logPosEvent({ clientId, actor, action: POS_AUDIT_ACTIONS.CATEGORY_REMOVED, entityId: name, summary: { name } });
  return next;
};

// ---- This client's own settings -------------------------------------------
//
// posTenants/<clientId>/config, NOT posClients/<clientId>. The registry is
// admin-only to write, and a club changing its own colours should not need an
// administrator to do it — so the theme lives in the client's own subtree,
// which its staff can already write.
//
// Global config (currency, the idle timeout, the receipt line) stays where it
// was, in posGlobalConfig: those are properties of the deployment, and two
// clients disagreeing about the currency would be a bug, not a preference.

/**
 * Live settings for one client. A missing node is normal — it means nobody has
 * opened Config yet — and reports as an empty object so the caller's defaults
 * stand.
 *
 * @param {string} clientId
 * @param {(config: object) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribePosClientConfig = (clientId, callback, onError) => onValue(
  tenantRef(clientId, POS_CONFIG_NODE),
  (snapshot) => callback(snapshot.val() || {}),
  (error) => {
    // A denied read must not be reported as "this client has no theme" — that
    // would repaint a club in the default colours for as long as a sign-out
    // takes. The caller keeps what it had.
    console.error('POS client config listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * Saves this client's theme.
 *
 * Only the five known theme keys are written, and each must be a hex colour.
 * Both checks matter for the same reason: these values end up inside a
 * <style> tag, so anything that is not a colour has no business being stored
 * as one.
 *
 * @param {string} clientId
 * @param {object} theme - keyed by POS_THEME_KEYS.
 * @param {string} [actor] - the staff address to record on the trail.
 * @returns {Promise<object>} what was saved
 * @throws {Error} user-facing, naming the first field that is not a hex colour
 */
export const savePosClientTheme = async (clientId, theme, actor = '') => {
  const clean = {};
  POS_THEME_KEYS.forEach((key) => {
    const value = String(theme?.[key] ?? '').trim().toLowerCase();
    if (!value) {
      // BLANK MEANS UNSET, and unset has to be writable — null is how Firebase
      // removes a child. Skipping empties instead (which is what this did)
      // made clearing a field impossible: the form showed it empty, the save
      // reported success, and the old colour was still in the database. It
      // matters most for Active Button Text Color, whose whole point is that
      // blank hands the decision back to the contrast calculation.
      clean[key] = null;
      return;
    }
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(value)) {
      throw userFacingError(`"${theme[key]}" is not a hex colour. Use a value like #6fb5ab.`);
    }
    clean[key] = value;
  });

  // update(), not set(): it leaves any other setting added to this node later
  // alone, so a future screen cannot be wiped by someone saving colours.
  await update(tenantRef(clientId, POS_CONFIG_NODE), clean);

  // The trail records what was SET. A cleared field is a null in the payload
  // above, and a row of nulls says nothing useful about what changed.
  const summary = Object.fromEntries(Object.entries(clean).filter(([, value]) => value !== null));
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.THEME_UPDATED,
    entityId: POS_CONFIG_NODE,
    summary,
  });
  return clean;
};

/**
 * How many products a screen should show at once, from this client's config.
 *
 * Returns 0 for "show every product", which is what an unset, blank, negative
 * or unparseable value means as well — one behaviour, reachable one way, and
 * the behaviour every client had before paging existed.
 *
 * `Number(null)` and `Number('')` are both 0, which would normally be a trap;
 * here it is harmless because 0 IS the fallback rather than a distinct
 * setting. (Contrast posStockLevel, where 0 means "warning off" and had to be
 * told apart from absent.)
 *
 * @param {object} [config] - the merged config from ConfigContext
 * @returns {number} a whole page size, or 0 for no limit
 */
export const posProductPageSize = (config) => {
  const size = Math.floor(Number(config?.[POS_PRODUCT_PAGE_SIZE_KEY]));
  if (!Number.isFinite(size) || size < POS_PRODUCT_PAGE_SIZE_MIN) return POS_PRODUCT_PAGE_SIZE_DEFAULT;
  return Math.min(size, POS_PRODUCT_PAGE_SIZE_MAX);
};

/**
 * Saves this client's product settings.
 *
 * A SEPARATE WRITE FROM THE THEME, into the same node. Both use update() and
 * touch only their own keys, so saving colours cannot wipe a page size and
 * saving a page size cannot wipe the colours — which is the whole reason that
 * node is written with update() rather than set().
 *
 * NOTE FOR DEPLOYING: every other key under posTenants/<id>/config is
 * validated in the rules as a hex colour. This one is a number, so the rules
 * carry a named `productPageSize` child of their own. A save here fails with
 * PERMISSION_DENIED until those rules are deployed.
 *
 * @param {string} clientId
 * @param {{productPageSize?: number|string}} settings
 * @param {string} [actor] - the staff address to record on the trail
 * @returns {Promise<object>} what was saved
 * @throws {Error} user-facing, on a page size that is not a whole number in range
 */
export const savePosClientSettings = async (clientId, settings, actor = '') => {
  const raw = settings?.[POS_PRODUCT_PAGE_SIZE_KEY];
  // Blank is "show everything" rather than an error: clearing the field is how
  // a club turns paging back off, and refusing it would leave no way to.
  const provided = typeof raw === 'number'
    || (typeof raw === 'string' && raw.trim() !== '');
  const size = provided ? Number(raw) : POS_PRODUCT_PAGE_SIZE_DEFAULT;

  if (!Number.isInteger(size) || size < 0 || size > POS_PRODUCT_PAGE_SIZE_MAX) {
    throw userFacingError(
      `Enter how many products to show per page as a whole number between ${POS_PRODUCT_PAGE_SIZE_MIN} and ${POS_PRODUCT_PAGE_SIZE_MAX}, or leave it blank to show every product.`,
    );
  }

  // Card size, when the caller sent it. Blank or 0 is "automatic".
  const cardSize = {};
  [
    [POS_CARD_WIDTH_KEY, POS_CARD_WIDTH_MIN, POS_CARD_WIDTH_MAX, 'width'],
    [POS_CARD_HEIGHT_KEY, POS_CARD_HEIGHT_MIN, POS_CARD_HEIGHT_MAX, 'height'],
    [POS_CARD_NAME_FONT_KEY, POS_CARD_FONT_MIN, POS_CARD_FONT_MAX, 'name font size'],
    [POS_CARD_PRICE_FONT_KEY, POS_CARD_FONT_MIN, POS_CARD_FONT_MAX, 'price font size'],
    [POS_CARD_DETAIL_FONT_KEY, POS_CARD_FONT_MIN, POS_CARD_FONT_MAX, 'details font size'],
  ].forEach(([key, min, max, word]) => {
    if (settings?.[key] === undefined) return;
    const { ok, value } = parsePosCardDimension(settings[key], min, max);
    if (!ok) {
      throw userFacingError(`Enter a card ${word} between ${min} and ${max} pixels, or leave it blank for automatic.`);
    }
    cardSize[key] = value;
  });

  const clean = {
    [POS_PRODUCT_PAGE_SIZE_KEY]: size,
    ...cardSize,
  };
  await update(tenantRef(clientId, POS_CONFIG_NODE), clean);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.SETTINGS_UPDATED,
    entityId: POS_CONFIG_NODE,
    // Spelled out, because "0" on a trail row is the one value a reader would
    // otherwise have to go and look up.
    summary: {
      productPageSize: size,
      meaning: size === 0 ? 'show every product' : `${size} per page`,
      ...(POS_CARD_WIDTH_KEY in cardSize
        ? { cardWidth: cardSize[POS_CARD_WIDTH_KEY] ? `${cardSize[POS_CARD_WIDTH_KEY]}px` : 'automatic' } : {}),
      ...(POS_CARD_HEIGHT_KEY in cardSize
        ? { cardHeight: cardSize[POS_CARD_HEIGHT_KEY] ? `${cardSize[POS_CARD_HEIGHT_KEY]}px` : 'automatic' } : {}),
      ...[
        [POS_CARD_NAME_FONT_KEY, 'nameFontSize'],
        [POS_CARD_PRICE_FONT_KEY, 'priceFontSize'],
        [POS_CARD_DETAIL_FONT_KEY, 'detailFontSize'],
      ].reduce((acc, [key, label]) => (key in cardSize
        ? { ...acc, [label]: cardSize[key] ? `${cardSize[key]}px` : 'automatic' }
        : acc), {}),
    },
  });
  return clean;
};

/**
 * Whether this client keeps a reserve (storeroom) stock.
 *
 * Switched by an administrator on the client form's Feature Settings tab
 * (saveClientFeatureSettings in posClientUtil.js), not by the club itself.
 *
 * Absent is ON: every client that existed before the setting ran with one,
 * and switching it off silently on upgrade would hide their storeroom.
 *
 * @param {object} config - from useConfig()
 * @returns {boolean}
 */
export const posReserveEnabled = (config) => config?.[POS_RESERVE_ENABLED_KEY] !== false;

// ---- Category icons -------------------------------------------------------
//
// Stored as ONE STRING per category, in a map keyed by the category name. The
// string is either an emoji or an Iconify id like "mdi:cup" — and the two are
// told apart by the colon, which an emoji never contains. That saves a wrapper
// object in every till's config for a value that is one token either way.

/**
 * Whether an icon value is an Iconify id rather than an emoji.
 *
 * @param {string} [value]
 * @returns {boolean}
 */
export const posIconIsIconify = (value) => typeof value === 'string' && value.includes(':');

/**
 * The SVG URL for an Iconify id.
 *
 * Used as a CSS mask rather than an <img>, so the icon takes currentColor and
 * inverts with the active category card — and so nothing fetched from a third
 * party is ever injected into this app's DOM as markup.
 *
 * @param {string} id - e.g. 'mdi:cup'
 * @returns {string}
 */
export const posIconifyUrl = (id) => `${POS_ICONIFY_BASE_URL}/${String(id).replace(':', '/')}.svg`;

/**
 * Live map of category name -> icon. A client that has never set one has no
 * node at all, which reports as {} and leaves every card on its default.
 *
 * @param {string} clientId
 * @param {(icons: Object<string, string>) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosCategoryIcons = (clientId, callback) => onValue(
  tenantRef(clientId, POS_CATEGORY_ICONS_NODE),
  (snapshot) => callback(snapshot.val() || {}),
  (error) => { console.error('POS category icons listener failed:', error); callback({}); },
);

/**
 * Sets or clears one category's icon.
 *
 * Writes the single key rather than the whole map: two people editing two
 * different categories at once would otherwise overwrite each other, each
 * saving the map as it looked when their picker opened.
 *
 * @param {string} clientId
 * @param {string} name - the category, exactly as stored in `categories`
 * @param {string} icon - an emoji or Iconify id; '' clears it
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const setPosCategoryIcon = async (clientId, name, icon, actor = '') => {
  const category = String(name || '').trim();
  if (!category) throw userFacingError('Choose a category first.');

  const value = String(icon || '').trim();
  if (value.length > POS_CATEGORY_ICON_MAX_LEN) {
    throw userFacingError('That icon reference is too long.');
  }

  // null DELETES the key in Realtime Database, which is what "no icon" is —
  // an empty string would leave a falsy value every reader has to test for.
  await set(tenantRef(clientId, `${POS_CATEGORY_ICONS_NODE}/${category}`), value || null);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.CATEGORY_ICON_SET,
    entityId: category,
    summary: { category, icon: value || '(cleared)' },
  });
};

/**
 * Searches Iconify for icons matching a query.
 *
 * Iconify's public API is used because it needs no key: the Noun Project and
 * Flaticon both require one, and a key shipped to a browser is a key given
 * away. Nothing here is stored — only the chosen id is, and the SVG is fetched
 * again at display time.
 *
 * @param {string} query - free text, e.g. 'drink'
 * @param {{signal?: AbortSignal}} [options] - to cancel a superseded keystroke
 * @returns {Promise<string[]>} Iconify ids, e.g. ['mdi:cup', 'ph:beer-bottle']
 */
export const searchPosIcons = async (query, { signal } = {}) => {
  const term = String(query || '').trim();
  if (!term) return [];

  const url = `${POS_ICONIFY_SEARCH_URL}?query=${encodeURIComponent(term)}&limit=${POS_ICONIFY_SEARCH_LIMIT}`;
  let response;
  try {
    response = await fetch(url, { signal });
  } catch (error) {
    // An aborted request is the caller superseding itself, not a failure to
    // report — it is rethrown so the caller can tell the two apart.
    if (error?.name === 'AbortError') throw error;
    throw userFacingError('Could not reach the icon service. Check the connection, or pick an emoji instead.');
  }
  if (!response.ok) {
    throw userFacingError('The icon service did not answer. Try again, or pick an emoji instead.');
  }

  const data = await response.json();
  return Array.isArray(data?.icons) ? data.icons : [];
};

// ---- Products -------------------------------------------------------------

/**
 * Live list of products, each with its Firebase `id`.
 * @param {string} clientId
 * @param {(products: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosProducts = (clientId, callback) => onValue(
  tenantRef(clientId, POS_PRODUCTS_NODE),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    callback(out);
  },
  (error) => { console.error('POS products listener failed:', error); callback([]); },
);

/**
 * Creates or updates a product.
 *
 * The image is compressed before storing, through the POS_PRODUCT preset —
 * the tightest one, because subscribePosProducts reads the WHOLE products
 * node, so every picture is downloaded again on every visit to every screen
 * that lists them. A full phone photo per product would make the till
 * unusable on mobile data.
 *
 * Stored as a base64 data URL on the record itself rather than as a binary
 * object in Firebase Storage. That is inherited from the booking system and
 * is the reason the ceiling matters so much: data URLs cost ~33% over the raw
 * bytes, are billed by the byte on every read, and are never browser-cached.
 * Moving these to Storage is the real fix if the catalogue grows.
 *
 * @param {string} clientId - the client whose catalogue this product belongs to
 * @param {object|null} existing - the product being edited, or null to create
 * @param {{name: string, description: string, category: string, price: number, imageFile: File|null, unitsPerPack?: number, unitLabel?: string, packLabel?: string, reservePacks?: number|string, reserveUnits?: number|string, customizations?: Array<object>}} details
 *   No opening stock: a new product's shelf always starts empty.
 *   `customizations` and `extras` follow the price rule: omitted, the stored
 *   ones are kept; an empty list removes them.
 *   `costPrice` (the "puhunan" for one) follows it too: omitted, the stored
 *   cost is kept; sent blank, it is cleared.
 * @returns {Promise<string>} the product's Firebase key
 */
export const savePosProduct = async (clientId, existing, details, actor = '') => {
  const name = String(details.name || '').trim();
  if (!name) throw userFacingError('Give the product a name.');

  // PRICE IS OPTIONAL HERE, and which of the three cases applies matters.
  //
  // It is edited on the Active tab, because a price is what a product SELLS
  // for and the shelf is where selling happens. The Reserve tab's form
  // therefore does not render the field at all — and a form that does not
  // render a field must not write one. Left out of an EDIT the stored price is
  // kept; left out of a REGISTRATION there is nothing to keep, so it starts at
  // 0 and is set when the product first reaches the shelf.
  const priceGiven = details.price !== undefined
    && details.price !== null
    && String(details.price).trim() !== '';
  const price = priceGiven ? Number(details.price) : (existing ? null : 0);
  if (priceGiven && (!Number.isFinite(price) || price < 0)) {
    throw userFacingError('Enter a price of 0 or more.');
  }

  // COST PRICE — what one costs the club. Same three cases as the price, with
  // one difference: blank is a real answer ("not costed yet") and CLEARS a
  // stored cost rather than writing 0, because a cost of 0 would make every
  // sale of it look like pure profit.
  const costGiven = details[POS_COST_PRICE_KEY] !== undefined;
  const costRaw = costGiven ? String(details[POS_COST_PRICE_KEY] ?? '').trim() : '';
  const costPrice = costRaw === '' ? null : Number(costRaw);
  if (costGiven && costPrice !== null && (!Number.isFinite(costPrice) || costPrice < 0)) {
    throw userFacingError('Enter a cost price of 0 or more, or leave it blank.');
  }

  // A NEW PRODUCT STARTS WITH AN EMPTY SHELF, always. Stock reaches the shelf
  // one way — replenishActiveFromReserve carrying it out of the storeroom — so
  // there is no opening figure to enter and no second way for the two counts
  // to be seeded out of step with each other. Registering happens on the
  // Reserve tab, where the opening RESERVE is asked for instead.
  //
  // THE ONE EXCEPTION is a client with NO reserve: there is no storeroom to
  // carry stock out of, so the shelf is where a product starts and its opening
  // count is asked for on the Active tab instead.
  const openingSuppliesRaw = details.openingSupplies;
  const openingGiven = !existing && openingSuppliesRaw !== undefined && String(openingSuppliesRaw).trim() !== '';
  const supplies = openingGiven ? Number(openingSuppliesRaw) : 0;
  if (openingGiven && (!Number.isInteger(supplies) || supplies < 0)) {
    throw userFacingError('Enter the opening stock as a whole number, 0 or more.');
  }

  // The opening RESERVE, also only meaningful when registering. Entered as
  // packs and/or pieces and stored, like every reserve, as one unit count.
  const openingReserve = existing ? 0 : posUnitsFromEntry(
    { packs: details.reservePacks, units: details.reserveUnits },
    Math.max(0, Math.floor(Number(details[POS_PACK_SIZE_KEY]) || 0)),
  );
  if (!existing && openingReserve > POS_RESERVE_UNITS_MAX) {
    throw userFacingError(`That is more reserve stock than this till can record (${POS_RESERVE_UNITS_MAX}).`);
  }

  // CUSTOMIZATION OPTIONS follow the price rule above: the Reserve tab's form
  // does not show them, so it does not send them, and an edit that did not
  // carry them must not touch them. Checked BEFORE the image is compressed,
  // so a bad option fails fast instead of after the slow part.
  const optionsGiven = Array.isArray(details[POS_CUSTOMIZATIONS_KEY]);
  const customizations = optionsGiven ? normalisePosCustomizations(details[POS_CUSTOMIZATIONS_KEY]) : null;
  // Extras ride along with the options — same form, same rule.
  const extrasGiven = Array.isArray(details[POS_EXTRAS_KEY]);
  const extras = extrasGiven ? normalisePosExtras(details[POS_EXTRAS_KEY]) : null;

  // Only re-encode when a NEW file was chosen; editing a product without
  // touching its picture keeps the stored image byte for byte rather than
  // recompressing an already-compressed image every time.
  let imageUrl = existing?.imageUrl || null;
  if (details.imageFile) {
    const { dataUrl } = await compressImage(details.imageFile, IMAGE_PRESETS.POS_PRODUCT);
    imageUrl = dataUrl;
  }

  // How this product is packed, and what its pieces are called. Descriptive
  // rather than a count, so unlike stock these ARE safe for an edit to write:
  // changing "24 to a box" to "12 to a box" is a correction about the product
  // itself, and nothing re-derives a stored quantity from it — the reserve is
  // held in units, so a changed pack size only changes how that number reads.
  const packSize = Math.min(Math.max(0, Math.floor(Number(details[POS_PACK_SIZE_KEY]) || 0)), POS_PACK_SIZE_MAX);
  const label = (value, fallback) => {
    const clean = String(value || '').trim().toLowerCase();
    return clean ? clean.slice(0, POS_STOCK_LABEL_MAX_LENGTH) : fallback;
  };

  const record = {
    name,
    description: String(details.description || '').trim(),
    category: String(details.category || '').trim(),
    // Omitted entirely when an edit did not carry one — update() leaves a key
    // it is not given alone, which is what keeps a price safe from a form that
    // never showed it.
    ...(price === null ? {} : { price }),
    // null removes a stored cost on an update, and is simply not stored on a
    // new product.
    ...(costGiven ? { [POS_COST_PRICE_KEY]: costPrice === null ? null : roundMoney(costPrice) } : {}),
    // null REMOVES the options on an update (and is simply not stored on a
    // new product), so unticking "With customization options" really clears
    // them rather than leaving an empty node behind.
    ...(optionsGiven ? { [POS_CUSTOMIZATIONS_KEY]: customizations.length > 0 ? customizations : null } : {}),
    ...(extrasGiven ? { [POS_EXTRAS_KEY]: extras.length > 0 ? extras : null } : {}),
    imageUrl,
    [POS_PACK_SIZE_KEY]: packSize,
    unitLabel: label(details.unitLabel, POS_UNIT_LABEL_DEFAULT),
    packLabel: label(details.packLabel, POS_PACK_LABEL_DEFAULT),
    createdAt: existing?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const id = existing?.id || push(tenantRef(clientId, POS_PRODUCTS_NODE)).key;

  // STOCK IS NOT WRITTEN BY AN EDIT, and this is the point of the whole
  // read-only field in the form.
  //
  // `supplies` is decremented by a transaction every time an order is placed.
  // A form that opened when there were 19 on the shelf and saved a full record
  // two minutes later would write 19 back — silently undoing every sale rung
  // up in between, and undoing them invisibly, since the number the person
  // saved is the number they were looking at.
  //
  // So an edit merges the descriptive fields and leaves stock alone. It
  // changes only through the two paths that read-modify-write it atomically:
  // placePosOrder taking some out, and addPosStock putting some in.
  if (existing) {
    await update(tenantRef(clientId, `${POS_PRODUCTS_NODE}/${id}`), record);
  } else {
    await set(tenantRef(clientId, `${POS_PRODUCTS_NODE}/${id}`), {
      ...record,
      supplies,
      reserveUnits: openingReserve,
    });
  }

  logPosEvent({
    clientId,
    actor,
    action: existing ? POS_AUDIT_ACTIONS.PRODUCT_UPDATED : POS_AUDIT_ACTIONS.PRODUCT_CREATED,
    entityId: id,
    // The image is deliberately left out of the summary — a base64 photo in
    // every audit row would make the trail enormous to read back.
    summary: {
      name: record.name,
      category: record.category,
      price: record.price,
      ...(costGiven ? { costPrice: costPrice === null ? 'Not set' : roundMoney(costPrice) } : {}),
      // The option labels, readable on the trail without opening the product.
      // Only when the form carried them, for the same reason as the price.
      ...(optionsGiven ? { options: customizations.map((option) => option.label).join(', ') || 'None' } : {}),
      ...(extrasGiven ? { extras: extras.map((item) => `${item.label} +${item.price}`).join(', ') || 'None' } : {}),
      // Only on a registration: an edit does not touch stock, and reporting a
      // figure it did not write would make the trail claim a change that
      // never happened.
      ...(existing
        ? {
          before: {
            name: existing.name,
            category: existing.category,
            price: existing.price,
            ...(costGiven ? { costPrice: existing[POS_COST_PRICE_KEY] ?? 'Not set' } : {}),
            ...(optionsGiven
              ? { options: posProductCustomizations(existing).map((option) => option.label).join(', ') || 'None' }
              : {}),
            ...(extrasGiven
              ? { extras: posProductExtras(existing).map((item) => `${item.label} +${item.price}`).join(', ') || 'None' }
              : {}),
          },
        }
        : { supplies }),
    },
  });

  return id;
};

/**
 * Adds stock to a product.
 *
 * A DELTA, never a new total. "20 arrived" and "there are now 39" are
 * different claims, and only the first is one the person holding the delivery
 * note can make correctly — the second silently depends on nothing having sold
 * since they last looked.
 *
 * Applied as a TRANSACTION, for the same reason: the till decrements the same
 * counter on every sale, so a read-then-write from a form that has been open
 * for two minutes would overwrite whatever was rung up in between. This adds
 * to whatever the database holds at the moment it commits, whatever that turns
 * out to be.
 *
 * @param {string} clientId
 * @param {object} product - the product, with `id`, `name` and `supplies`
 * @param {number|string} quantity - how many to add; a whole number, 1 or more
 * @param {string} [actor] - the staff address to record on the trail
 * @param {object} [options]
 * @param {boolean} [options.fromReserve] - move the units out of the reserve
 *   instead of adding new ones
 * @param {boolean} [options.skippedReserve] - this client keeps a reserve, but
 *   the delivery went straight onto the shelf ("Take from reserve" unticked).
 *   Recorded on the trail, so a shelf that grew while the reserve did not is
 *   explained rather than looking like a miscount.
 * @returns {Promise<number>} the stock level after the addition
 * @throws {Error} user-facing, on a quantity that is not a positive whole number
 */
export const addPosStock = async (clientId, product, quantity, actor = '', { fromReserve = false, skippedReserve = false } = {}) => {
  if (!product?.id) throw userFacingError('Save the product before adding stock to it.');

  const added = Number(quantity);
  if (!Number.isInteger(added) || added < 1) {
    throw userFacingError('Enter how many are being added, as a whole number of 1 or more.');
  }

  // WITH A RESERVE, stock reaching the shelf comes OUT of the storeroom — so
  // this is a move, not a delivery, and it is refused when the reserve is
  // short. replenishActiveFromReserve already does exactly that in one
  // transaction; the shelf figure after the move is what the caller wants.
  if (fromReserve) {
    const { active } = await replenishActiveFromReserve(clientId, product, { units: added }, actor);
    return active;
  }

  const stockRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/supplies`);
  const result = await runTransaction(stockRef, (current) => {
    // A missing or corrupt count is treated as zero rather than left alone:
    // the delivery happened either way, and refusing to record it would leave
    // the shelf stocked and the system saying nothing is there.
    const now = Math.max(0, Math.floor(Number(current) || 0));
    return now + added;
  });

  if (!result.committed) throw userFacingError('Could not add stock just now. Please try again.');

  const after = Number(result.snapshot.val()) || 0;
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.STOCK_ADDED,
    entityId: product.id,
    // `before` is derived from what actually committed, not from what the form
    // was showing — those differ by exactly the sales the trail is there to
    // account for.
    summary: {
      name: product.name,
      added,
      before: after - added,
      after,
      ...(skippedReserve ? { source: 'Straight to the shelf (reserve skipped)' } : {}),
    },
  });

  return after;
};

/**
 * Sets a product's stock to an exact count — a correction, not a delivery.
 *
 * ABSOLUTE, where addPosStock is a delta. The two answer different questions:
 * "twelve arrived" is something the delivery note says, and "there are
 * nineteen on the shelf" is something a stocktake says. Both are legitimate;
 * only the second can overrule what the till believed.
 *
 * Still a TRANSACTION, so the value that was actually replaced is the one the
 * trail records — the count may have moved between the form opening and this
 * committing, and "corrected 24 -> 19" is only useful if the 24 is true.
 *
 * WRITTEN ONLY WHEN SOMEONE CHANGED THE FIELD. The caller compares against what
 * it displayed and skips this entirely otherwise, which is what stops an edit
 * to a product's name from quietly reinstating a stale count and undoing every
 * sale rung up while the form was open.
 *
 * @param {string} clientId
 * @param {object} product - the product, with `id`, `name` and `supplies`
 * @param {number|string} count - the new stock level; a whole number, 0 or more
 * @param {string} [actor] - the staff address to record on the trail
 * @returns {Promise<number>} the stock level after the correction
 * @throws {Error} user-facing, on a count that is not a whole number of 0 or more
 */
export const setPosStock = async (clientId, product, count, actor = '', { fromReserve = false } = {}) => {
  if (!product?.id) throw userFacingError('Save the product before correcting its stock.');
  if (fromReserve) return setPosStockFromReserve(clientId, product, count, actor);

  // EMPTY IS NOT ZERO. Number('') and Number(null) are both 0, and 0 is a
  // legitimate count — so a cleared field would sail through and silently wipe
  // the product's stock. A value has to be present before it is converted.
  const provided = typeof count === 'number'
    || (typeof count === 'string' && count.trim() !== '');
  const next = provided ? Number(count) : NaN;
  if (!Number.isInteger(next) || next < 0) {
    throw userFacingError('Enter available supplies as a whole number, 0 or more.');
  }

  const stockRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/supplies`);
  // Captured inside the updater rather than read beforehand: this is the value
  // the write actually replaced, which is the only one worth reporting.
  let before = 0;
  const result = await runTransaction(stockRef, (current) => {
    before = Math.max(0, Math.floor(Number(current) || 0));
    return next;
  });

  if (!result.committed) throw userFacingError('Could not update the stock just now. Please try again.');

  const after = Number(result.snapshot.val()) || 0;
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.STOCK_ADJUSTED,
    entityId: product.id,
    summary: { name: product.name, before, after, change: after - before },
  });

  return after;
};

/**
 * setPosStock for a client that keeps a reserve.
 *
 * RAISING the shelf count takes the difference out of the reserve, in the same
 * transaction, and is refused if the reserve is short — the extra units had to
 * come from somewhere, and the storeroom is the only place they can. LOWERING
 * it is a plain correction (breakage, a miscount) and leaves the reserve alone.
 *
 * @param {string} clientId
 * @param {object} product
 * @param {number|string} count - the new shelf count
 * @param {string} [actor]
 * @returns {Promise<number>} the shelf count after the change
 * @throws {Error} user-facing, on a bad count or a short reserve
 */
const setPosStockFromReserve = async (clientId, product, count, actor = '') => {
  const provided = typeof count === 'number'
    || (typeof count === 'string' && count.trim() !== '');
  const next = provided ? Number(count) : NaN;
  if (!Number.isInteger(next) || next < 0) {
    throw userFacingError('Enter available supplies as a whole number, 0 or more.');
  }

  const productRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}`);
  let before = { active: 0, reserve: 0 };
  let shortOf = null;
  const result = await runTransaction(productRef, (current) => {
    if (!current) return undefined;
    const active = wholeUnits(current.supplies);
    const reserve = wholeUnits(current.reserveUnits);
    const taken = Math.max(0, next - active);
    if (taken > reserve) {
      shortOf = { reserve, taken };
      return undefined;
    }
    before = { active, reserve };
    return { ...current, supplies: next, reserveUnits: reserve - taken };
  });

  if (!result.committed) {
    if (shortOf) {
      throw userFacingError(
        `Only ${describePosUnits(shortOf.reserve, product)} left in reserve — not enough to add ${shortOf.taken} to the shelf.`,
      );
    }
    throw userFacingError('Could not update the stock just now. Please try again.');
  }

  const saved = result.snapshot.val() || {};
  const after = { active: wholeUnits(saved.supplies), reserve: wholeUnits(saved.reserveUnits) };
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.STOCK_ADJUSTED,
    entityId: product.id,
    summary: {
      name: product.name,
      before: before.active,
      after: after.active,
      change: after.active - before.active,
      ...(after.reserve !== before.reserve
        ? { takenFromReserve: before.reserve - after.reserve, reserveAfter: after.reserve }
        : {}),
    },
  });
  return after.active;
};

/**
 * Whether a product is out, running low, or fine.
 *
 * ONE definition for both screens. The till draws a banner and Inventory turns
 * the count red, and they have to agree about what "low" means — two copies of
 * `supplies <= 5` would not, the first time the threshold moved.
 *
 * OUT beats LOW: at zero there is nothing to run low on, and the screens
 * already say "Out of stock" plainly. A threshold of 0 turns the warning off
 * without turning off the out-of-stock state, which is a fact rather than a
 * preference.
 *
 * @param {object} product - a POS product, with `supplies`.
 * @param {number|string} [threshold] - config.lowStockThreshold.
 * @returns {'out'|'low'|'ok'}
 */
export const posStockLevel = (product, threshold = POS_LOW_STOCK_DEFAULT) => {
  const supplies = Math.max(0, Math.floor(Number(product?.supplies) || 0));
  if (supplies <= 0) return 'out';

  // ABSENT IS NOT ZERO, and Number() disagrees: Number(null), Number(undefined
  // via '') and Number('') are all 0, which is the value that means "turn the
  // warning off". Taken at face value, a config that had not loaded yet — or
  // any deployment whose global config predates this setting — would silently
  // show no low-stock warnings at all. So a usable value is required before
  // anything is converted.
  const usable = typeof threshold === 'number'
    || (typeof threshold === 'string' && threshold.trim() !== '');
  const limit = usable ? Number(threshold) : NaN;

  // Anything still unreadable falls back to the default rather than to
  // silence: a broken setting should not quietly stop the till saying it is
  // running out.
  const effective = Number.isInteger(limit) && limit >= 0 ? limit : POS_LOW_STOCK_DEFAULT;
  return supplies <= effective ? 'low' : 'ok';
};

/**
 * The payment method record for a stored key.
 *
 * Returns null for an order recorded before payment methods existed, which is
 * what lets the receipt leave the "Paid via" row off entirely rather than
 * inventing a method those orders never captured.
 *
 * @param {string} [key] - a POS_PAYMENT_METHODS key, e.g. 'gcash'
 * @returns {{key: string, label: string, emoji: string}|null}
 */


/**
 * How many more of a product the till may still add to the order in progress.
 *
 * Stock MINUS what this order has already claimed, never the raw `supplies`
 * figure. Six on the shelf with four already in the basket leaves two — a cap
 * of six there would let one order promise eight, and the shortfall would only
 * surface when placePosOrder's transaction refused the whole sale at the
 * counter, with the customer waiting.
 *
 * @param {object} product - a POS product, with `id` and `supplies`
 * @param {Object<string, number>} [cart] - { [productId]: quantity } in progress
 * @returns {number} whole units still addable, never negative
 */
export const posRemainingStock = (product, cart = {}) => {
  const supplies = Math.max(0, Math.floor(Number(product?.supplies) || 0));
  const claimed = Math.max(0, Math.floor(Number(cart?.[product?.id]) || 0));
  return Math.max(0, supplies - claimed);
};

/**
 * Deletes a product. Past ORDERS are untouched — they store their own copy of
 * the name and price, so a receipt printed last week still reads correctly
 * after the product it sold has gone.
 *
 * @param {object} product
 * @returns {Promise<void>}
 */
export const deletePosProduct = async (clientId, product, actor = '') => {
  if (!product?.id) throw new Error('Product id is required.');
  await remove(tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}`));
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.PRODUCT_DELETED,
    entityId: product.id,
    summary: { name: product.name, category: product.category, price: product.price, supplies: product.supplies },
  });
};

// ---- VAT ------------------------------------------------------------------
//
// ONE PLACE ONLY. The order rail, the confirmation receipt, the recorded order
// and the receipt read back from Sales all call posVatBreakdown, so the four
// cannot quote different figures for the same basket — which is the failure
// that matters here, because the customer sees two of them and the books see
// the other two.

/**
 * Which VAT mode a client is on, as a value this module recognises.
 *
 * Anything unset, unknown or left over from an older record reads as "no VAT"
 * rather than as a guess: charging tax nobody configured is the one wrong
 * answer that takes money off a customer.
 *
 * @param {object} [config] - the merged config (ConfigContext) or an order
 * @returns {string} one of POS_VAT_MODE_*
 */
export const posVatMode = (config) => {
  const mode = String(config?.vatMode || '').trim();
  return POS_VAT_MODE_KEYS.includes(mode) ? mode : POS_VAT_MODE_NONE;
};

/**
 * The VAT percentage a client charges, as a number between 0 and the maximum.
 *
 * Returns 0 for anything unusable, which turns VAT off — same reasoning as
 * above. Note that `Number('')` and `Number(null)` are both 0 and 0 is a
 * meaningful rate here (it means "no tax"), so nothing is lost by it, unlike
 * the low-stock threshold where 0 had to be told apart from absent.
 *
 * @param {object} [config]
 * @returns {number} a percentage, e.g. 12
 */
export const posVatRate = (config) => {
  const rate = Number(config?.vatRate);
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return Math.min(rate, POS_VAT_RATE_MAX);
};

/**
 * Splits a basket subtotal into its item-cost and VAT parts.
 *
 * `net + vat === total` ALWAYS, to the centavo. The tax figure is rounded
 * first and the other part is derived by subtraction, rather than rounding
 * both independently — two independently rounded halves can miss their own
 * total by a centavo, and a receipt whose lines do not add up is the one thing
 * a customer will always spot.
 *
 * The three modes differ only in what the subtotal already contains:
 *   none       total = subtotal, no tax line
 *   inclusive  total = subtotal; the tax came OUT of it (see
 *              POS_VAT_INCLUSIVE_METHOD for how the split is worked out)
 *   exclusive  total = subtotal + tax; the tax went ON TOP
 *
 * @param {number|string} subtotal - what the items came to
 * @param {{vatMode?: string, vatRate?: number|string}} [config] - the client's
 *   VAT settings, from ConfigContext or captured on a stored order
 * @returns {{mode: string, rate: number, applies: boolean, subtotal: number, net: number, netShare: number, vat: number, total: number}}
 *   `applies` is false when there is no tax line to print at all. `netShare`
 *   is what percentage of an inclusive subtotal the item cost came to — the
 *   "(88%)" on the breakdown line — DERIVED from the figures rather than from
 *   100 minus the rate, so the label stays true if POS_VAT_INCLUSIVE_METHOD
 *   is ever switched to 'divisor', where the two are not the same number.
 */
export const posVatBreakdown = (subtotal, config = {}) => {
  const base = roundMoney(subtotal);
  const mode = posVatMode(config);
  const rate = posVatRate(config);

  // A zero rate, an unconfigured client, or an empty basket: nothing to split.
  // Reported as the same shape so callers never branch on a missing field.
  if (mode === POS_VAT_MODE_NONE || rate <= 0 || base <= 0) {
    return { mode, rate, applies: false, subtotal: base, net: base, netShare: 100, vat: 0, total: base };
  }

  if (mode === POS_VAT_MODE_INCLUSIVE) {
    const vat = POS_VAT_INCLUSIVE_METHOD === 'divisor'
      ? roundMoney(base - (base / (1 + (rate / 100))))
      : roundMoney(base * (rate / 100));
    const net = roundMoney(base - vat);
    return {
      mode, rate, applies: true, subtotal: base, net, vat, total: base,
      netShare: roundMoney((net / base) * 100),
    };
  }

  const vat = roundMoney(base * (rate / 100));
  return {
    mode, rate, applies: true, subtotal: base, net: base, netShare: 100, vat,
    total: roundMoney(base + vat),
  };
};

// ---- Orders ---------------------------------------------------------------

/**
 * Takes payment for a basket: decrements stock, then records the order.
 *
 * STOCK FIRST, AND ONE TRANSACTION PER PRODUCT. Each decrement re-reads the
 * live count inside the transaction and aborts if the basket wants more than
 * is there, so two staff charging the last item at the same moment cannot
 * both succeed — a plain read-then-write would let them. Anything already
 * decremented when a later line fails is PUT BACK before throwing, so a
 * refused sale leaves stock exactly as it found it.
 *
 * The order stores its own copy of each product's name and price. Prices
 * change and products get deleted; a receipt has to keep saying what was
 * actually charged on the day.
 *
 * VAT IS CAPTURED ON THE ORDER, not looked up when the order is read back.
 * The mode and rate live on the client profile and an administrator can change
 * them at any time; without a copy here, every receipt ever printed would
 * silently restate itself at the new rate the moment it did — and the figures
 * on a customer's paper slip would stop matching the ones in the system.
 *
 * CUSTOMIZED LINES. A line may carry `options` — the answered customization
 * options from resolvePosSelections. Their extra prices are added to the
 * item's unit `price` (the base is kept as `basePrice`), and the answers are
 * stored on the item, so the receipt says "50% sugar, pearl" for as long as
 * the order exists — whatever the product's options become later. The same
 * product can appear on several lines with different answers; each line takes
 * its own units off the one shelf count.
 *
 * @param {Array<{product: object, quantity: number, options?: Array<{label: string, value: string, price: number}>}>} lines
 * @param {{cashReceived?: number, deliveryFee?: number}} [payment] - cash
 *   tendered, for the CASH / CHANGE lines on the receipt (omitted for a card or
 *   e-wallet sale, where printing a change figure would state something untrue
 *   about it), and an optional delivery fee added on top of the goods.
 * @param {string} [actor] - the staff address to record as the cashier
 * @param {{vatMode?: string, vatRate?: number}} [vatConfig] - this client's VAT
 *   settings. Omitted or unset means no VAT, which is what every client that
 *   has not been configured for it gets.
 * @returns {Promise<{id: string, order: object}>}
 * @throws when a line exceeds available stock — the message names the product
 */
export const placePosOrder = async (clientId, lines, payment = {}, actor = '', vatConfig = {}) => {
  const basket = (lines || []).filter(l => l?.product?.id && Number(l.quantity) > 0);
  if (basket.length === 0) throw userFacingError('Add at least one product before charging.');

  const decremented = [];
  try {
    for (const { product, quantity } of basket) {
      const qty = Number(quantity);
      const stockRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/supplies`);
      // eslint-disable-next-line no-await-in-loop
      const result = await runTransaction(stockRef, (current) => {
        const available = Number(current) || 0;
        // undefined ABORTS — the only way to say "not enough" from inside a
        // transaction without writing something wrong.
        if (available < qty) return undefined;
        return available - qty;
      });
      if (!result.committed) {
        throw userFacingError(`Not enough stock for ${product.name}. Please check Inventory and try again.`);
      }
      decremented.push({ product, qty });
    }
  } catch (error) {
    // Give back every unit this attempt took, so a half-applied basket never
    // leaves the shelf count short.
    await Promise.all(decremented.map(({ product, qty }) => runTransaction(
      tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/supplies`),
      (current) => (Number(current) || 0) + qty,
    ).catch(() => {})));
    throw error;
  }

  const items = basket.map(({ product, quantity, options }) => {
    const basePrice = Number(product.price) || 0;
    const chosen = (options || []).map((option) => ({
      label: String(option.label || ''),
      value: String(option.value || ''),
      price: roundMoney(Number(option.price) || 0),
      // An extra's own name, count per item and price per piece, for the
      // receipt's breakdown rows. null (not undefined — Firebase refuses
      // undefined) on an ordinary option, which Firebase then drops.
      name: option.name ? String(option.name) : null,
      quantity: Number(option.quantity) > 0 ? Number(option.quantity) : null,
      unitPrice: Number.isFinite(Number(option.unitPrice)) && option.unitPrice !== undefined && option.unitPrice !== null
        ? roundMoney(Number(option.unitPrice))
        : null,
    }));
    const extra = roundMoney(chosen.reduce((sum, option) => sum + option.price, 0));
    const price = roundMoney(basePrice + extra);
    return {
      productId: product.id,
      name: product.name,
      category: product.category || '',
      price,
      quantity: Number(quantity),
      lineTotal: roundMoney(price * Number(quantity)),
      // WHAT ONE COST at the time of the sale, copied like the price is, so
      // a later change to the product's cost does not rewrite the profit on
      // orders already taken. The product's own cost only — options and
      // extras have no cost of their own. null when none was entered.
      unitCost: Number.isFinite(Number(product[POS_COST_PRICE_KEY])) && product[POS_COST_PRICE_KEY] !== null
        && product[POS_COST_PRICE_KEY] !== undefined && product[POS_COST_PRICE_KEY] !== ''
        ? roundMoney(Number(product[POS_COST_PRICE_KEY]))
        : null,
      // Only on a customized line, so an ordinary order keeps exactly the
      // shape orders always had (Firebase drops the nulls).
      basePrice: chosen.length > 0 ? basePrice : null,
      options: chosen.length > 0 ? chosen : null,
    };
  });

  const subtotal = roundMoney(items.reduce((sum, item) => sum + item.lineTotal, 0));
  const vat = posVatBreakdown(subtotal, vatConfig);
  // `total` stays the name of WHAT THE CUSTOMER PAID, which is what every
  // report, chart and export already sums — so on VAT Exclusive it is the
  // subtotal plus the tax, not the subtotal. The pre-tax figure is stored
  // alongside as `subtotal` rather than replacing it.
  // DELIVERY IS CHARGED ON TOP, outside VAT: it is a service passed on to the
  // customer, not a taxed line item. Refused rather than clamped when it is
  // not a sensible amount, so the receipt never quietly disagrees with what
  // the cashier typed.
  const rawFee = Number(payment?.deliveryFee || 0);
  if (!Number.isFinite(rawFee) || rawFee < 0 || rawFee > POS_DELIVERY_FEE_MAX) {
    throw userFacingError(`Enter a delivery fee between 0 and ${POS_DELIVERY_FEE_MAX}.`);
  }
  const deliveryFee = roundMoney(rawFee);
  const total = roundMoney(vat.total + deliveryFee);
  // Resolved rather than stored as given: this string is what Sales groups a
  // day's takings by, and one stray value would become a category of its own
  // that no report knows how to show. The client's own list is consulted
  // first, so a method they added is a real method here too.
  const resolved = posPaymentMethod(payment?.method, payment?.methods);
  const method = resolved?.key || POS_DEFAULT_PAYMENT_METHOD;
  // THE LABEL IS CAPTURED, like the VAT rate beside it. A club can rename
  // "GCash" or drop it entirely, and a receipt printed today has to keep
  // saying what the customer actually paid with.
  const methodLabel = resolved?.label || posPaymentMethod(method)?.label || '';

  const cashReceived = Number(payment?.cashReceived);
  // Only a cash sale tenders an amount. A GCash sale must not record cash and
  // change it never took, however the field was left.
  const paidCash = method === POS_CASH_METHOD && Number.isFinite(cashReceived) && cashReceived > 0;

  const order = {
    paymentMethod: method,
    paymentLabel: methodLabel || null,
    items,
    subtotal,
    // Written only when tax was actually charged. Firebase drops null children,
    // so an order taken by a client with no VAT keeps exactly the shape orders
    // had before VAT existed — and the receipt decides whether to print a tax
    // breakdown on precisely this.
    vat: vat.applies ? vat.vat : null,
    vatNet: vat.applies ? vat.net : null,
    vatRate: vat.applies ? vat.rate : null,
    vatMode: vat.applies ? vat.mode : null,
    // null, not 0, when there was none — Firebase drops it, so an order with
    // no delivery keeps the shape orders always had.
    deliveryFee: deliveryFee > 0 ? deliveryFee : null,
    total,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
    // null rather than 0 when no cash was taken: 0 would be indistinguishable
    // from a sale where the customer handed over nothing, and the receipt
    // decides whether to print CASH/CHANGE on exactly this.
    cashReceived: paidCash ? cashReceived : null,
    // Floored at 0 — a short payment must never record NEGATIVE change, which
    // would read as the till owing the customer money.
    //
    // ROUNDED, because this figure is STORED and read back on every receipt.
    // ₱200 tendered against a ₱156.80 total is 43.19999999999999 in binary
    // floating point; it printed as ₱43.20 because formatMoney rounds on the
    // way to the screen, but the database held the dust. VAT made that common
    // rather than rare — a tax-exclusive total lands on a whole number about
    // as often as not.
    change: paidCash ? roundMoney(Math.max(0, cashReceived - total)) : null,
    cashier: resolveActor(actor),
    createdAt: new Date().toISOString(),
    createdAtMs: Date.now(),
  };

  const orderRef = push(tenantRef(clientId, POS_ORDERS_NODE));
  await set(orderRef, order);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.ORDER_PLACED,
    entityId: orderRef.key,
    summary: {
      total: order.total,
      itemCount: order.itemCount,
      // Readable in the trail without a lookup table, and searchable there:
      // "gcash" typed into the audit search should find these orders.
      payment: posPaymentMethod(method)?.label || method,
      // Only on a sale that charged tax. A `vat: 0` on every row of a club
      // that does not charge VAT would make the trail harder to read for the
      // clubs that do.
      ...(vat.applies ? { subtotal, vat: vat.vat, vatRate: vat.rate, vatMode: vat.mode } : {}),
      ...(deliveryFee > 0 ? { deliveryFee } : {}),
      items: items.map(i => `${i.quantity}x ${posOrderItemLabel(i)}`),
    },
  });

  return { id: orderRef.key, order };
};

/**
 * Live list of recent orders, newest first.
 *
 * Bounded by POS_RECENT_ORDERS_LIMIT: the Sales screen is a review of trading,
 * not an archive, and an unbounded node would grow without limit and be
 * re-downloaded in full on every visit.
 *
 * @param {string} clientId
 * @param {(orders: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosOrders = (clientId, callback) => onValue(
  query(tenantRef(clientId, POS_ORDERS_NODE), orderByChild('createdAtMs'), limitToLast(POS_RECENT_ORDERS_LIMIT)),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.reverse();
    callback(out);
  },
  (error) => { console.error('POS orders listener failed:', error); callback([]); },
);

// ---- Deleting a sale -------------------------------------------------------
//
// A SOFT delete — a status change only. The order is a record of money taken
// and stays exactly as it was written; deleting it adds a `deletion` child
// (reason, who, when) that the database rules let be written ONCE and never
// changed. Nothing else is touched: no stock moves. Every report reads that
// marker and leaves the sale out, while the Sales screen can still list it
// under Cancelled/Deleted with the reason beside it.

/**
 * A stored ISO date or epoch ms as the "Oct 16, 2026, 1:47 AM" the Sales
 * screen prints everywhere.
 *
 * @param {string|number} value
 * @returns {string} '' when the value is not a date
 */
export const posDateTimeText = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * Whether an order has been deleted (cancelled).
 *
 * @param {object} order - an order as read from POS_ORDERS_NODE
 * @returns {boolean}
 */
export const posOrderIsDeleted = (order) => Boolean(order?.[POS_ORDER_DELETION_KEY]);

/**
 * Deletes (cancels) one sale, with the reason it was deleted.
 *
 * Marks rather than removes — see the block comment above. The marker is
 * written in a TRANSACTION so two people deleting the same sale at once get
 * a clear "already deleted" rather than the second silently replacing the
 * first person's reason (the rules would refuse it anyway, but with a
 * permission error that reads like a deployment problem).
 *
 * Stock is NOT touched: this is purely a change of status.
 *
 * @param {string} clientId
 * @param {object} order - the order to delete; needs `id` and `items`
 * @param {string} reason - why it is being deleted; required
 * @param {string} [actor] - the staff address doing it, for the record and audit trail
 * @returns {Promise<{deletion: object}>} the marker as written
 * @throws a user-facing error for a missing/too long reason or an already-deleted sale
 */
export const deletePosOrder = async (clientId, order, reason, actor = '') => {
  if (!order?.id) throw new Error('Order id is required.');
  if (posOrderIsDeleted(order)) throw userFacingError('This sale has already been deleted.');

  const text = String(reason || '').trim();
  if (text.length < POS_DELETE_REASON_MIN_LENGTH) {
    throw userFacingError(`Enter a reason of at least ${POS_DELETE_REASON_MIN_LENGTH} characters.`);
  }
  if (text.length > POS_DELETE_REASON_MAX_LENGTH) {
    throw userFacingError(`Keep the reason to ${POS_DELETE_REASON_MAX_LENGTH} characters or fewer.`);
  }

  const deletedAtMs = Date.now();
  const deletion = {
    reason: text,
    deletedBy: resolveActor(actor),
    deletedAt: new Date(deletedAtMs).toISOString(),
    deletedAtMs,
  };

  const result = await runTransaction(
    tenantRef(clientId, `${POS_ORDERS_NODE}/${order.id}/${POS_ORDER_DELETION_KEY}`),
    // undefined ABORTS: someone else's deletion is already there.
    (current) => (current ? undefined : deletion),
  );
  if (!result.committed) throw userFacingError('This sale was already deleted by someone else. Fetch the sales again to see it.');

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.ORDER_DELETED,
    entityId: order.id,
    summary: {
      reason: text,
      total: order.total,
      itemCount: order.itemCount,
      payment: order.paymentLabel || posPaymentMethod(order.paymentMethod)?.label || order.paymentMethod || '',
      cashier: order.cashier || '',
      soldAt: order.createdAt || '',
      items: (order.items || []).map(i => `${i.quantity}x ${posOrderItemLabel(i)}`),
    },
  });

  return { deletion };
};

/**
 * One order flattened to the text the Sales search box matches against:
 * its id, items (with their options), cashier, payment method and — for a
 * deleted sale — the reason and who deleted it.
 *
 * @param {object} order
 * @returns {string} lower-cased haystack
 */
export const posOrderSearchText = (order) => {
  const deletion = order?.[POS_ORDER_DELETION_KEY];
  return [
    order?.id,
    ...(order?.items || []).map((item) => posOrderItemLabel(item)),
    order?.cashier,
    order?.paymentLabel || order?.paymentMethod,
    deletion?.reason,
    deletion?.deletedBy,
  ].filter(Boolean).join(' ').toLowerCase();
};

/**
 * The sales the Sales preview lists, after its own status and search filters.
 *
 * Runs in the browser over orders already fetched for the range, so typing
 * costs no database read. Empty terms are skipped rather than matching
 * nothing.
 *
 * @param {object[]} orders - already scoped by payment method and staff
 * @param {object} [filters]
 * @param {string} [filters.status] - a POS_SALE_STATUS_* value; anything else means all
 * @param {string} [filters.orderId] - matched against the order id only
 * @param {string} [filters.search] - matched against posOrderSearchText
 * @returns {object[]} the surviving orders, order preserved
 */
export const filterPosSaleRecords = (orders, filters = {}) => {
  const { status = POS_SALE_STATUS_COMPLETED, orderId = '', search = '' } = filters;
  const idTerm = String(orderId).trim().toLowerCase();
  const term = String(search).trim().toLowerCase();
  const useId = idTerm.length >= POS_SEARCH_MIN_CHARS;
  const useSearch = term.length >= POS_SEARCH_MIN_CHARS;

  return (orders || []).filter((order) => {
    const deleted = posOrderIsDeleted(order);
    if (status === POS_SALE_STATUS_COMPLETED && deleted) return false;
    if (status === POS_SALE_STATUS_DELETED && !deleted) return false;
    if (useId && !String(order?.id || '').toLowerCase().includes(idTerm)) return false;
    if (useSearch && !posOrderSearchText(order).includes(term)) return false;
    return true;
  });
};

/**
 * Live POS audit entries, newest first.
 * @param {string} clientId
 * @param {(entries: object[]) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosAudit = (clientId, callback) => onValue(
  query(tenantRef(clientId, POS_AUDIT_NODE), orderByChild('timestamp'), limitToLast(POS_RECENT_ORDERS_LIMIT)),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
    out.reverse();
    callback(out);
  },
  (error) => { console.error('POS audit listener failed:', error); callback([]); },
);

/**
 * A 'yyyy-MM-dd' string as the first millisecond of that LOCAL day.
 *
 * Local rather than UTC because these strings come from <input type="date">,
 * which speaks the reader's own calendar day — bounding it in another zone
 * would silently move an evening's records into the wrong day.
 *
 * @param {string} value - 'yyyy-MM-dd'
 * @returns {number|null} epoch ms, or null when the string is not a date
 */
export const dayStartMs = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d, 0, 0, 0, 0).getTime() : null;
};

/**
 * A 'yyyy-MM-dd' string as the last millisecond of that LOCAL day, so a range
 * that ends today includes everything recorded today.
 *
 * @param {string} value - 'yyyy-MM-dd'
 * @returns {number|null} epoch ms, or null when the string is not a date
 */
export const dayEndMs = (value) => {
  const [y, m, d] = String(value || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime() : null;
};

/**
 * One audit entry flattened to the text a reader would search it by.
 *
 * VALUES only, never the field names: the summaries are keyed by things like
 * 'name' and 'items', so including keys would make a search for "name" match
 * every row and teach staff the box is useless. The values are where the
 * products, categories and prices actually live.
 *
 * @param {object} entry - an audit entry as stored under POS_AUDIT_NODE
 * @returns {string} lower-cased haystack
 */
export const posAuditSearchText = (entry) => {
  const parts = [POS_AUDIT_LABELS[entry?.action] || entry?.action || '', entry?.performedBy || ''];
  const summary = entry?.summary;

  if (summary && typeof summary === 'object') {
    Object.values(summary).forEach((value) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) parts.push(value.join(' '));
      // 'before' on a product update is a nested object of the old values —
      // worth searching, so the old name of a renamed product still finds it.
      else if (typeof value === 'object') parts.push(Object.values(value).join(' '));
      else parts.push(String(value));
    });
  }

  return parts.join(' ').toLowerCase();
};

/**
 * The audit entries a set of filters leaves visible.
 *
 * Filtered in the browser rather than server-side on purpose: the trail is
 * already capped at POS_RECENT_ORDERS_LIMIT entries by subscribePosAudit, so
 * this is a small in-memory list, and a live subscription cannot be re-queried
 * per keystroke without dropping and re-opening the listener.
 *
 * Every filter is optional and they compose — an empty or malformed one is
 * skipped rather than matching nothing, so a half-typed date never blanks the
 * screen.
 *
 * @param {object[]} entries - the full list, newest first
 * @param {object} [filters]
 * @param {string} [filters.action] - a POS_AUDIT_ACTIONS value; '' for all
 * @param {string} [filters.search] - free text matched against posAuditSearchText
 * @param {string} [filters.startDate] - 'yyyy-MM-dd', from 00:00 local
 * @param {string} [filters.endDate] - 'yyyy-MM-dd', to 23:59:59.999 local
 * @returns {object[]} the surviving entries, order preserved
 */
export const filterPosAuditEntries = (entries, filters = {}) => {
  const { action = '', search = '', startDate = '', endDate = '' } = filters;

  const term = String(search).trim().toLowerCase();
  const useSearch = term.length >= POS_SEARCH_MIN_CHARS;
  const from = dayStartMs(startDate);
  const to = dayEndMs(endDate);

  return (entries || []).filter((entry) => {
    if (action && entry.action !== action) return false;
    if (from !== null && Number(entry.timestamp) < from) return false;
    if (to !== null && Number(entry.timestamp) > to) return false;
    if (useSearch && !posAuditSearchText(entry).includes(term)) return false;
    return true;
  });
};

/**
 * Orders placed between two dates, inclusive of both.
 *
 * A one-shot RANGE query rather than the live subscription above: a report is
 * a question about a fixed period, and streaming it would have the numbers
 * shift under the reader as sales come in. Bounded server-side on
 * createdAtMs (see the .indexOn in database.rules.json) so a wide date range
 * doesn't pull the whole node down to filter it in the browser.
 *
 * @param {string} startDateStr - 'yyyy-MM-dd', from 00:00 local
 * @param {string} endDateStr - 'yyyy-MM-dd', to 23:59:59.999 local
 * @returns {Promise<object[]>} newest first
 */
export const fetchPosOrdersInRange = async (clientId, startDateStr, endDateStr) => {
  const from = dayStartMs(startDateStr);
  const to = dayEndMs(endDateStr);
  if (from === null || to === null) throw userFacingError('Choose a valid start and end date.');
  if (from > to) throw userFacingError('The start date is after the end date.');

  const snapshot = await get(query(
    tenantRef(clientId, POS_ORDERS_NODE),
    orderByChild('createdAtMs'),
    startAt(from),
    endAt(to),
  ));

  const out = [];
  snapshot.forEach((child) => { out.push({ id: child.key, ...child.val() }); });
  out.reverse();
  return out;
};

/**
 * What ONE unit of an order line cost the club, for the Sales screen's cost
 * columns.
 *
 * The cost copied onto the line when it was sold wins — it is what the item
 * actually cost on the day. Lines sold before cost prices existed have none,
 * so the product's CURRENT cost price is used for them instead (through
 * `costLookup`): an estimate, but a far more useful one than leaving the
 * whole history blank. null when neither is known.
 *
 * @param {{productId?: string, unitCost?: number|null}} item - an order line
 * @param {Map<string, number>} [costLookup] - product id -> current cost price
 * @returns {number|null}
 */
export const posItemUnitCost = (item, costLookup) => {
  const stored = item?.unitCost;
  if (stored !== null && stored !== undefined && stored !== '' && Number.isFinite(Number(stored))) {
    return Number(stored);
  }
  const current = costLookup?.get(item?.productId);
  return current !== null && current !== undefined && Number.isFinite(Number(current)) ? Number(current) : null;
};

/**
 * What one order's items cost the club — the sum of each line's unit cost
 * times its quantity. Lines with no known cost are left out; null when none
 * of its lines has one, so "unknown" is never shown as ₱0.
 *
 * @param {object} order
 * @param {Map<string, number>} [costLookup] - see posItemUnitCost
 * @returns {number|null}
 */
export const posOrderCost = (order, costLookup) => {
  let cost = 0;
  let known = false;
  (order?.items || []).forEach((item) => {
    const unit = posItemUnitCost(item, costLookup);
    if (unit === null) return;
    known = true;
    cost += unit * (Number(item.quantity) || 0);
  });
  return known ? roundMoney(cost) : null;
};

/**
 * Builds the product id -> current cost price map the cost helpers read.
 *
 * @param {object[]} products - as from subscribePosProducts
 * @returns {Map<string, number>} only products that have a cost price
 */
export const posCostLookup = (products) => new Map(
  (products || [])
    .filter((p) => p?.id && p[POS_COST_PRICE_KEY] !== null && p[POS_COST_PRICE_KEY] !== undefined
      && p[POS_COST_PRICE_KEY] !== '' && Number.isFinite(Number(p[POS_COST_PRICE_KEY])))
    .map((p) => [p.id, Number(p[POS_COST_PRICE_KEY])]),
);

/**
 * Revenue broken down by category, then by product within it.
 *
 * Mirrors the booking export's Source -> Component table: the category is the
 * source, each product a component under it. Shares are of the grand total,
 * so the components sum to 100% across the whole report rather than within
 * their own category.
 *
 * TWO TOTALS, AND THEY ARE NOT THE SAME NUMBER ONCE VAT IS EXCLUSIVE.
 *
 *   total       the products' own revenue — every line total added up. This
 *               is the one the category and product rows sum to, and the one
 *               their shares are computed against, because an ITEM carries no
 *               tax of its own to apportion.
 *   grossTotal  what the customers actually paid — every order's total added
 *               up. On VAT Exclusive this is `total` plus the tax.
 *   vat         the tax charged across the range, whichever mode it was.
 *
 * Keeping both is what stops the Sales screen contradicting itself: the
 * breakdown table has to add up to the rows above it, while the orders table
 * beneath has to add up to the figures on its own rows. Reporting one number
 * for both made the footer disagree with the column over it the moment a club
 * switched VAT on.
 *
 * COST, when asked for, is added alongside: each product's average unit cost
 * and its total cost, each category's cost, and the whole range's. A product
 * none of whose lines has a known cost reports null rather than 0 — see
 * posItemUnitCost.
 *
 * @param {object[]} orders
 * @param {{costLookup?: Map<string, number>}} [options] - product id -> current
 *   cost price, the fallback for lines sold before costs were recorded
 * @returns {{total: number, grossTotal: number, vat: number, deliveryFees: number, itemCount: number, orderCount: number, cost: number|null, groups: Array<{name: string, revenue: number, share: number, cost: number|null, products: Array<{name: string, revenue: number, quantity: number, share: number, unitCost: number|null, cost: number|null}>}>}}
 */
export const summarisePosSales = (orders, { costLookup } = {}) => {
  const byCategory = new Map();
  let total = 0;
  let grossTotal = 0;
  let vat = 0;
  let deliveryFees = 0;
  let itemCount = 0;
  let cost = 0;
  let costKnown = false;

  (orders || []).forEach((order) => {
    let orderItems = 0;
    vat += Number(order?.vat) || 0;
    deliveryFees += Number(order?.deliveryFee) || 0;

    (order?.items || []).forEach((item) => {
      const revenue = Number(item.lineTotal) || 0;
      const quantity = Number(item.quantity) || 0;
      total += revenue;
      orderItems += revenue;
      itemCount += quantity;

      // Products saved before categories existed, or whose category was
      // removed, still have to appear somewhere or the table stops adding up.
      const categoryName = item.category || 'Uncategorised';
      const group = byCategory.get(categoryName)
        || { name: categoryName, revenue: 0, products: new Map() };
      group.revenue += revenue;

      const key = item.productId || item.name;
      const product = group.products.get(key)
        || { name: item.name, revenue: 0, quantity: 0, cost: 0, costUnits: 0 };
      product.revenue += revenue;
      product.quantity += quantity;

      // Cost only where it is known, and counted against only the units it
      // is known for — so the average unit cost is not dragged down by lines
      // that simply have no cost recorded.
      const unitCost = posItemUnitCost(item, costLookup);
      if (unitCost !== null) {
        const lineCost = unitCost * quantity;
        product.cost += lineCost;
        product.costUnits += quantity;
        group.cost = (group.cost || 0) + lineCost;
        group.costKnown = true;
        cost += lineCost;
        costKnown = true;
      }
      product.name = item.name || product.name;
      group.products.set(key, product);
      byCategory.set(categoryName, group);
    });

    // Falls back to this order's own items when `total` is missing, which is
    // the shape of a record written before totals were stored at all. Better a
    // gross figure that matches the net one than a row counted as zero.
    const paid = Number(order?.total);
    grossTotal += Number.isFinite(paid) ? paid : orderItems;
  });

  const share = (value) => (total > 0 ? (value / total) * 100 : 0);

  const groups = [...byCategory.values()]
    .map(group => ({
      name: group.name,
      revenue: group.revenue,
      share: share(group.revenue),
      cost: group.costKnown ? roundMoney(group.cost) : null,
      products: [...group.products.values()]
        .map(({ costUnits, ...p }) => ({
          ...p,
          share: share(p.revenue),
          cost: costUnits > 0 ? roundMoney(p.cost) : null,
          unitCost: costUnits > 0 ? roundMoney(p.cost / costUnits) : null,
          // Derived from the revenue actually taken rather than read off the
          // product record: the price may have been edited part-way through
          // the period, and the figure the report owes the reader is what
          // these units really sold for, averaged.
          unitPrice: p.quantity > 0 ? roundMoney(p.revenue / p.quantity) : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue),
    }))
    .sort((a, b) => b.revenue - a.revenue);

  return {
    total: roundMoney(total),
    grossTotal: roundMoney(grossTotal),
    vat: roundMoney(vat),
    // Inside grossTotal, outside `total` — the goods breakdown never includes
    // it, so the screen names it whenever the two differ because of it.
    deliveryFees: roundMoney(deliveryFees),
    itemCount,
    orderCount: (orders || []).length,
    cost: costKnown ? roundMoney(cost) : null,
    groups,
  };
};

/**
 * The local date a bucket of this granularity starts on.
 *
 * Local, not UTC, for the same reason the range inputs are: a sale rung up at
 * 9pm belongs to the day the cashier thinks it does.
 *
 * @param {Date} date - any moment inside the bucket
 * @param {string} granularity - a POS_SALES_GRANULARITIES key, or POS_HOURLY_GRANULARITY
 * @returns {Date} the start of the bucket — midnight, or the top of the hour when hourly
 */
const bucketStart = (date, granularity) => {
  if (granularity === POS_HOURLY_GRANULARITY) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours());
  }
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (granularity === 'weekly') {
    // Monday-start weeks. getDay() calls Sunday 0, so it is rotated to 6 —
    // otherwise a Sunday would open its own week rather than closing the one
    // before it.
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  } else if (granularity === 'monthly') {
    d.setDate(1);
  } else if (granularity === 'yearly') {
    d.setMonth(0, 1);
  }
  return d;
};

/**
 * The start of the bucket after this one.
 *
 * Stepped with setMonth/setFullYear rather than by adding milliseconds, so
 * month lengths and daylight-saving changes are the calendar's problem rather
 * than this function's.
 *
 * @param {Date} date - a bucket start
 * @param {string} granularity - a POS_SALES_GRANULARITIES key, or POS_HOURLY_GRANULARITY
 * @returns {Date}
 */
const nextBucket = (date, granularity) => {
  const d = new Date(date);
  if (granularity === POS_HOURLY_GRANULARITY) d.setHours(d.getHours() + 1);
  else if (granularity === 'weekly') d.setDate(d.getDate() + 7);
  else if (granularity === 'monthly') d.setMonth(d.getMonth() + 1);
  else if (granularity === 'yearly') d.setFullYear(d.getFullYear() + 1);
  else d.setDate(d.getDate() + 1);
  return d;
};

/**
 * How a bucket is named on the x axis.
 *
 * @param {Date} date - a bucket start
 * @param {string} granularity - a POS_SALES_GRANULARITIES key, or POS_HOURLY_GRANULARITY
 * @returns {string} e.g. '1 PM', 'Sep 15', 'Sep 2026', '2026'
 */
const bucketLabel = (date, granularity) => {
  if (granularity === POS_HOURLY_GRANULARITY) return date.toLocaleTimeString('en-US', { hour: 'numeric' });
  if (granularity === 'yearly') return String(date.getFullYear());
  if (granularity === 'monthly') return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * A bucket start as a lookup key. The hour is included so hourly buckets on
 * the same day stay distinct; every other granularity starts at midnight, so
 * for them it is always 0.
 *
 * @param {Date} date - a bucket start
 * @returns {string} e.g. '2026-9-22-13'
 */
const bucketKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}`;

/**
 * Takings over time, one point per period, for the Sales trend line.
 *
 * EVERY period in the range gets a point, including the ones that sold
 * nothing. A line that simply skipped quiet days would join Friday to Monday
 * as though the weekend had been busy — the flat stretch at zero is the
 * finding, not noise to drop.
 *
 * Each point carries BOTH measures and the per-category split, so the screen's
 * Amount/Quantity and Overall/Category toggles re-read what is already here
 * rather than refetching or rebucketing.
 *
 * On amount, the OVERALL figure is what customers actually paid (the order's
 * stored total, tax included) so the line agrees with the headline above it,
 * while the per-category figures are product revenue — the only amount a
 * category can own. On VAT-exclusive clients the categories therefore sum to
 * less than the overall point, which is why `vat` is carried alongside: the
 * hover can show the tax as its own line and still add up.
 *
 * @param {object[]} orders - as returned by fetchPosOrdersInRange
 * @param {string} granularity - a POS_SALES_GRANULARITIES key, or POS_HOURLY_GRANULARITY
 * @param {{from: string, to: string}} range - the report period, YYYY-MM-DD
 * @param {{costLookup?: Map<string, number>}} [options] - see posItemUnitCost;
 *   each point's `cost` is what its items cost, null when none is known
 * @returns {{points: Array<{key: string, label: string, amount: number, quantity: number, vat: number, orderCount: number, cost: number|null, byCategory: Object<string, {amount: number, quantity: number}>}>, categories: string[], truncated: boolean}}
 *   `categories` is every category seen, ordered by total revenue so the
 *   colour slots are assigned to the biggest sellers first. `truncated` when
 *   the range needed more buckets than POS_TREND_MAX_BUCKETS at this
 *   granularity, so the caller can say so rather than plot a fraction of the
 *   period as if it were all of it.
 */
export const bucketPosSalesOverTime = (orders, granularity, range = {}, { costLookup } = {}) => {
  const fromMs = dayStartMs(range.from);
  const toMs = dayEndMs(range.to);
  if (fromMs === null || toMs === null || fromMs > toMs) {
    return { points: [], categories: [], truncated: false };
  }

  // Built empty first, then filled: this is what puts the zero periods on the
  // line, and it also fixes the x axis to the REPORT's period rather than to
  // whichever days happened to have sales.
  const points = [];
  let cursor = bucketStart(new Date(fromMs), granularity);
  let truncated = false;
  while (cursor.getTime() <= toMs) {
    if (points.length >= POS_TREND_MAX_BUCKETS) { truncated = true; break; }
    points.push({
      key: bucketKey(cursor),
      label: bucketLabel(cursor, granularity),
      startMs: cursor.getTime(),
      amount: 0,
      quantity: 0,
      vat: 0,
      orderCount: 0,
      cost: null,
      byCategory: {},
    });
    cursor = nextBucket(cursor, granularity);
  }

  // Indexed rather than scanned: a year of daily buckets against a busy range
  // turned the per-order lookup into a linear search through 365 points.
  const byKey = new Map(points.map(p => [p.key, p]));
  const categoryTotals = new Map();

  (orders || []).forEach((order) => {
    const ms = Number(order?.createdAtMs) || Date.parse(order?.createdAt);
    if (!Number.isFinite(ms)) return;

    const start = bucketStart(new Date(ms), granularity);
    const point = byKey.get(bucketKey(start));
    if (!point) return;

    const items = order?.items || [];
    // Falls back to the items' own sum for records written before totals were
    // stored, mirroring summarisePosSales so the two never disagree.
    const paid = Number(order?.total);
    point.amount += Number.isFinite(paid)
      ? paid
      : items.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0);
    point.vat += Number(order?.vat) || 0;
    point.orderCount += 1;
    const orderCost = posOrderCost(order, costLookup);
    if (orderCost !== null) point.cost = (point.cost || 0) + orderCost;

    items.forEach((item) => {
      const quantity = Number(item.quantity) || 0;
      const revenue = Number(item.lineTotal) || 0;
      point.quantity += quantity;

      // Products saved before categories existed, or whose category was
      // removed, still have to appear somewhere or the lines stop adding up.
      const name = item.category || 'Uncategorised';
      const bucket = point.byCategory[name] || { amount: 0, quantity: 0 };
      bucket.amount += revenue;
      bucket.quantity += quantity;
      point.byCategory[name] = bucket;

      categoryTotals.set(name, (categoryTotals.get(name) || 0) + revenue);
    });
  });

  return {
    points: points.map(p => ({
      ...p,
      amount: roundMoney(p.amount),
      vat: roundMoney(p.vat),
      cost: p.cost === null ? null : roundMoney(p.cost),
      byCategory: Object.fromEntries(
        Object.entries(p.byCategory).map(([name, v]) => [name, { ...v, amount: roundMoney(v.amount) }]),
      ),
    })),
    // Biggest first, so the fixed colour slots go to the categories a reader
    // is most likely to be looking for.
    categories: [...categoryTotals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name),
    truncated,
  };
};

/**
 * The key one cashier's orders are grouped under.
 *
 * Lower-cased, because Firebase Auth echoes back whatever casing an account
 * was created with — "Bryan@club.com" and "bryan@club.com" are one person, and
 * two rows in a staff filter would be two ways to filter for the same takings.
 *
 * @param {string} [cashier] - the address recorded on an order
 * @returns {string} '' when there is nothing usable to key on
 */
export const posCashierKey = (cashier) => String(cashier || '').trim().toLowerCase();

/**
 * Who took money in the given orders, with how much each of them took.
 *
 * READ OFF THE ORDERS, not off the client's staff list. Those are different
 * questions: the staff list is who may stand at the till today, this is who
 * actually rang something up in the period being reported on. Someone who has
 * left still appears against the weeks they worked — which is the whole point
 * of being able to filter a past range by them — and someone hired yesterday
 * does not clutter a report on last month.
 *
 * Sorted by takings, biggest first, so the people a report is mostly about are
 * at the front of the filter rather than wherever the alphabet puts them.
 *
 * @param {object[]} orders
 * @returns {Array<{key: string, label: string, orderCount: number, total: number}>}
 */
export const listPosCashiers = (orders) => {
  const byKey = new Map();
  (orders || []).forEach((order) => {
    const key = posCashierKey(order?.cashier);
    if (!key) return;
    const row = byKey.get(key) || { key, label: String(order.cashier).trim(), orderCount: 0, total: 0 };
    row.orderCount += 1;
    row.total += Number(order?.total) || 0;
    byKey.set(key, row);
  });
  return [...byKey.values()]
    .map((row) => ({ ...row, total: roundMoney(row.total) }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
};

/**
 * The orders taken by a chosen set of cashiers.
 *
 * TAKES THE EXCLUDED SET, not the included one, and that is deliberate. A
 * report's staff list is rebuilt from whatever range is loaded, so a filter
 * that remembered who was TICKED would silently drop anyone who first appears
 * in a newly-fetched range — the one person a widened range was opened to look
 * at. Remembering who was un-ticked instead means a new name arrives included,
 * which is the answer someone widening a range is asking for.
 *
 * @param {object[]} orders
 * @param {Set<string>} [excluded] - cashier keys to leave out (see posCashierKey)
 * @returns {object[]} a new array; the input is never modified
 */
export const filterPosOrdersByCashier = (orders, excluded) => {
  if (!excluded || excluded.size === 0) return [...(orders || [])];
  return (orders || []).filter((order) => !excluded.has(posCashierKey(order?.cashier)));
};

/**
 * Every product that appears in the given orders, with what it sold.
 *
 * Keyed by productId with the name carried alongside, so a renamed product
 * stays ONE row rather than splitting into two — the orders hold the name as
 * it was on the day, and a product that has been relabelled twice would
 * otherwise rank as three different things.
 *
 * Falls back to the name as the key for a line recorded before products had
 * ids. That merges two different products that happened to share a name, which
 * is wrong but rare, and is still better than dropping the line.
 *
 * @param {object[]} orders
 * @returns {Map<string, {productId: string, name: string, quantity: number, revenue: number}>}
 */
const tallyOrderItems = (orders) => {
  const totals = new Map();
  (orders || []).forEach((order) => {
    // A deleted sale sold nothing — it must not lift a product up the
    // till's "best selling" order or the best-seller export.
    if (posOrderIsDeleted(order)) return;
    (order?.items || []).forEach((item) => {
      const key = item.productId || item.name;
      const row = totals.get(key) || { productId: key, name: item.name, quantity: 0, revenue: 0 };
      row.quantity += Number(item.quantity) || 0;
      row.revenue += Number(item.lineTotal) || 0;
      // The most recent name wins, so the chart matches what the product is
      // called today.
      row.name = item.name || row.name;
      totals.set(key, row);
    });
  });
  return totals;
};

/**
 * Units sold per product id, for ordering the till's grid by what moves.
 *
 * A Map rather than a sorted list because the caller is sorting PRODUCTS, not
 * order lines: a product that has never sold has no row here at all and must
 * still appear in the grid, so the lookup has to answer "how many?" for a key
 * it has never seen. Callers read it as `map.get(id) || 0`.
 *
 * @param {object[]} orders
 * @returns {Map<string, number>}
 */
export const tallyPosUnitsSold = (orders) => new Map(
  [...tallyOrderItems(orders)].map(([key, row]) => [key, row.quantity]),
);

/**
 * Units sold per product across the given orders, best first.
 *
 * @param {object[]} orders
 * @param {number} limit
 * @returns {Array<{productId: string, name: string, quantity: number, revenue: number}>}
 */
export const rankBestSellers = (orders, limit) => [...tallyOrderItems(orders).values()]
  .sort((a, b) => b.quantity - a.quantity)
  .slice(0, limit);

// ---- Payment methods ------------------------------------------------------

/**
 * The payment methods this client offers, live.
 *
 * FALLS BACK TO THE BUILT-IN LIST when the client has never edited them, so a
 * till that nobody has configured still takes cash — an empty node must not
 * mean "no way to pay".
 *
 * @param {string} clientId
 * @param {(methods: Array<{key: string, label: string, icon: string}>) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosPaymentMethods = (clientId, callback) => onValue(
  tenantRef(clientId, POS_PAYMENT_METHODS_NODE),
  (snapshot) => callback(normalisePaymentMethods(snapshot.val())),
  (error) => {
    console.error('POS payment methods listener failed:', error);
    callback(normalisePaymentMethods(null));
  },
);

/**
 * Whatever is stored, as a usable list.
 *
 * Anything missing, empty or unusable reads as the built-in list. A counter
 * that cannot take money is worse than a counter offering the wrong buttons.
 *
 * @param {*} value - the raw node
 * @returns {Array<{key: string, label: string, icon: string}>}
 */
const normalisePaymentMethods = (value) => {
  const list = Array.isArray(value) ? value : [];
  const clean = list
    .map((entry) => ({
      key: String(entry?.key || '').trim(),
      label: String(entry?.label || '').trim(),
      icon: String(entry?.icon || '').trim(),
    }))
    .filter((entry) => entry.key && entry.label);
  return clean.length > 0
    ? clean
    : POS_PAYMENT_METHODS.map(m => ({ key: m.key, label: m.label, icon: m.emoji || '' }));
};

/**
 * Saves this client's payment methods.
 *
 * KEYS ARE PERMANENT once a sale has used one: an order stores the key it was
 * taken under, so renaming a method is a label change and deleting one does
 * not rewrite history. Editing the label of "gcash" is therefore safe; what is
 * NOT safe is reusing that key for something else, which is why a key is
 * derived from the label only when a method is first added and then left
 * alone.
 *
 * @param {string} clientId
 * @param {Array<{key: string, label: string, icon?: string}>} methods
 * @param {string} [actor]
 * @returns {Promise<Array<object>>} what was saved
 * @throws {Error} user-facing, on an empty list, a duplicate key or a long label
 */
export const savePosPaymentMethods = async (clientId, methods, actor = '') => {
  const list = (methods || [])
    .map((entry) => ({
      key: String(entry?.key || '').trim().toLowerCase(),
      label: String(entry?.label || '').trim(),
      icon: String(entry?.icon || '').trim(),
    }))
    .filter((entry) => entry.key || entry.label);

  if (list.length === 0) {
    throw userFacingError('Keep at least one payment method — a counter has to be able to take money somehow.');
  }
  if (list.length > POS_PAYMENT_METHODS_MAX) {
    throw userFacingError(`That is more than ${POS_PAYMENT_METHODS_MAX} payment methods. The confirm step becomes a menu rather than a choice.`);
  }

  const seen = new Set();
  list.forEach((entry) => {
    if (!entry.label) throw userFacingError('Give every payment method a name, or remove the empty row.');
    if (entry.label.length > POS_PAYMENT_LABEL_MAX_LENGTH) {
      throw userFacingError(`"${entry.label}" is too long — keep a payment name under ${POS_PAYMENT_LABEL_MAX_LENGTH} characters.`);
    }
    if (!entry.key) throw userFacingError(`"${entry.label}" has no key. Remove the row and add it again.`);
    if (seen.has(entry.key)) {
      throw userFacingError(`Two methods share the key "${entry.key}". Rename one of them.`);
    }
    seen.add(entry.key);
    if (entry.icon.length > POS_CATEGORY_ICON_MAX_LEN) {
      throw userFacingError('That icon reference is too long.');
    }
  });

  await set(tenantRef(clientId, POS_PAYMENT_METHODS_NODE), list);
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.PAYMENTS_UPDATED,
    entityId: POS_PAYMENT_METHODS_NODE,
    summary: { count: list.length, methods: list.map(m => m.label) },
  });
  return list;
};

/**
 * A storage key derived from a payment method's label.
 *
 * ONLY EVER FOR A METHOD THAT HAS NONE YET. A key that already exists is
 * permanent — orders were written against it, and Sales groups a day's
 * takings by it — so re-deriving one from an edited label would silently
 * orphan every sale taken under the old name.
 *
 * Lives here rather than in the form because that rule is worth a test, and a
 * rule enforced only inside a component is a rule nothing checks.
 *
 * @param {string} label - what the club typed
 * @returns {string} lower-case, hyphenated, at most 24 characters
 */
export const posPaymentMethodKey = (label) => String(label || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 24)
  // A label of nothing but punctuation trims down to a bare hyphen, which is
  // a key that looks empty in the audit trail and sorts oddly in reports.
  .replace(/^-+|-+$/g, '');

/**
 * Turns a method key into something printable.
 *
 * Looks in the client's own list first, then the built-in one, and finally
 * invents an entry from the key itself. That last step is what keeps an OLD
 * RECEIPT readable after the method it was taken under has been renamed or
 * removed — the alternative is a blank where the payment line should be.
 *
 * @param {string} key - as stored on the order
 * @param {Array<object>} [methods] - the client's live list
 * @returns {{key: string, label: string, icon: string}|null} null for no key
 */
export const posPaymentMethod = (key, methods) => {
  const wanted = String(key || '').trim();
  if (!wanted) return null;

  const fromClient = (methods || []).find(m => m.key === wanted);
  if (fromClient) return fromClient;

  const builtIn = POS_PAYMENT_METHODS.find(m => m.key === wanted);
  if (builtIn) return { key: builtIn.key, label: builtIn.label, icon: builtIn.emoji || '' };

  // Title-cased from the key, so "bank_transfer" prints as "Bank Transfer"
  // rather than disappearing.
  const label = wanted
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { key: wanted, label, icon: '' };
};

// ---- Store details --------------------------------------------------------

/**
 * This client's logo and typeface, live. `{}` until set, which means "inherit".
 *
 * @param {string} clientId
 * @param {(branding: object) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosBranding = (clientId, callback) => onValue(
  tenantRef(clientId, POS_BRANDING_NODE),
  (snapshot) => callback(snapshot.val() || {}),
  (error) => {
    console.error('POS branding listener failed:', error);
    callback({});
  },
);

/**
 * The CSS stack for a stored font choice.
 *
 * An unknown key reads as the default rather than as nothing: a font key that
 * no longer exists must not leave the app with no font-family at all.
 *
 * @param {string} [key]
 * @returns {string}
 */
export const posFontStack = (key) => {
  // An uploaded font: its registered family first, the default stack behind
  // it for the moment before the file has loaded (or if it never does).
  if (typeof key === 'string' && key.startsWith(POS_CUSTOM_FONT_PREFIX)) {
    const id = key.slice(POS_CUSTOM_FONT_PREFIX.length).replace(/[^A-Za-z0-9_-]/g, '');
    const fallback = POS_FONT_CHOICES.find(f => f.key === POS_FONT_DEFAULT).stack;
    return `'${POS_CUSTOM_FONT_FAMILY_PREFIX}${id}', ${fallback}`;
  }
  const choice = POS_FONT_CHOICES.find(f => f.key === key)
    || POS_FONT_CHOICES.find(f => f.key === POS_FONT_DEFAULT);
  return choice.stack;
};

/**
 * Saves this client's logo and typeface.
 *
 * THE LOGO IS COMPRESSED HERE, through the same LOGO preset the admin form
 * uses: every till session for this client downloads it, so an unresized phone
 * photo would be paid for on every load.
 *
 * A null logoFile leaves the stored logo alone; `clearLogo` is how it is
 * actually removed. Two different intentions, and conflating them would make
 * "change the font" quietly delete the logo.
 *
 * @param {string} clientId
 * @param {{logoFile?: File|null, clearLogo?: boolean, fontFamily?: string}} details
 * @param {string} [actor]
 * @returns {Promise<object>} what was written
 * @throws {Error} user-facing, on an unusable font choice
 */
export const savePosBranding = async (clientId, details, actor = '') => {
  const payload = {};

  if (details?.fontFamily !== undefined) {
    const font = String(details.fontFamily || '').trim();
    // A built-in key, or "custom-<id>" for a font this club uploaded. The
    // custom id is held to the characters a push id uses, so nothing else can
    // ride into the stylesheet through it.
    const isCustom = font.startsWith(POS_CUSTOM_FONT_PREFIX)
      && /^[A-Za-z0-9_-]+$/.test(font.slice(POS_CUSTOM_FONT_PREFIX.length))
      && font.length <= 32;
    if (font && !POS_FONT_KEYS.includes(font) && !isCustom) {
      throw userFacingError('Pick one of the listed typefaces.');
    }
    payload.fontFamily = font || POS_FONT_DEFAULT;
  }

  if (details?.clearLogo) {
    // null DELETES the key, which is what "no logo" is — the client then falls
    // back to whatever the administrator set.
    payload.logoUrl = null;
  } else if (details?.logoFile) {
    const { dataUrl } = await compressImage(details.logoFile, IMAGE_PRESETS.LOGO);
    payload.logoUrl = dataUrl;
  }

  if (Object.keys(payload).length === 0) return {};

  // update(), not set(): saving a font must not clear a logo, and the reverse.
  await update(tenantRef(clientId, POS_BRANDING_NODE), payload);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.BRANDING_UPDATED,
    entityId: POS_BRANDING_NODE,
    // The image is left out — a base64 photo on every audit row would make the
    // trail enormous to read back.
    summary: {
      ...(payload.fontFamily ? { fontFamily: payload.fontFamily } : {}),
      ...(details?.clearLogo ? { logo: 'cleared' } : {}),
      ...(details?.logoFile ? { logo: 'replaced' } : {}),
    },
  });
  return payload;
};

// ---- Reserve stock --------------------------------------------------------
//
// See the block comment in constants/posConst.js for why the reserve is one
// number of units rather than a boxes/loose pair. Everything below either
// derives that split for display or moves units across it.

/**
 * A count as a whole number of units, floored at 0.
 * @param {*} value
 * @returns {number}
 */
const wholeUnits = (value) => Math.max(0, Math.floor(Number(value) || 0));

/**
 * How many units are in one unopened pack of this product.
 *
 * 0 means the product has no pack at all — it is counted only in pieces, and
 * every "boxes" control is hidden for it rather than offering a box that
 * holds nothing.
 *
 * @param {object} [product]
 * @returns {number} 0 when unset or unusable
 */
export const posPackSize = (product) => {
  const size = wholeUnits(product?.[POS_PACK_SIZE_KEY]);
  return Math.min(size, POS_PACK_SIZE_MAX);
};

/** This product's reserve, in units. @param {object} [product] @returns {number} */
export const posReserveUnits = (product) => wholeUnits(product?.reserveUnits);

/** This product's active stock, in units — the one sales draw on. */
export const posActiveUnits = (product) => wholeUnits(product?.supplies);

/**
 * Splits a unit count into whole packs and the pieces left over.
 *
 * With no pack size everything is loose, which is the honest answer for a
 * product sold only in pieces rather than a division by zero.
 *
 * @param {number} units
 * @param {number} packSize
 * @returns {{packs: number, loose: number}}
 */
export const posSplitPacks = (units, packSize) => {
  const total = wholeUnits(units);
  const size = wholeUnits(packSize);
  if (size <= 0) return { packs: 0, loose: total };
  return { packs: Math.floor(total / size), loose: total % size };
};

/**
 * Pluralises a noun the way English mostly does.
 *
 * Deliberately small: these nouns are typed by a club ("bottle", "sachet",
 * "box", "case"), and -s/-x/-ch/-sh covers every one of them. Anything
 * irregular is the club's own word and it can type the plural it wants into
 * the label.
 *
 * @param {string} noun
 * @param {number} count
 * @returns {string}
 */
const pluralise = (noun, count) => {
  const word = String(noun || '').trim();
  if (count === 1 || !word) return word;
  return /(s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`;
};

/**
 * What this product calls one piece, pluralised for the count.
 * @param {object} [product]
 * @param {number} [count=2]
 * @returns {string}
 */
export const posUnitNoun = (product, count = 2) => pluralise(
  String(product?.unitLabel || '').trim() || POS_UNIT_LABEL_DEFAULT,
  count,
);

/**
 * What this product calls one unopened pack, pluralised for the count.
 * @param {object} [product]
 * @param {number} [count=2]
 * @returns {string}
 */
export const posPackNoun = (product, count = 2) => pluralise(
  String(product?.packLabel || '').trim() || POS_PACK_LABEL_DEFAULT,
  count,
);

/**
 * A unit count in this product's own words: "3 boxes and 10 bottles".
 *
 * WHOLE PACKS FIRST, because that is how a storeroom is read — nobody counts
 * 82 bottles, they see three boxes and a few loose. A product with no pack
 * size reads as a plain count, and an empty one says so rather than printing
 * "0 boxes and 0 pieces".
 *
 * @param {number} units
 * @param {object} [product] - for the pack size and the nouns
 * @returns {string}
 */
export const describePosUnits = (units, product) => {
  const total = wholeUnits(units);
  const size = posPackSize(product);
  if (total === 0) return `No ${posUnitNoun(product, 2)}`;

  const { packs, loose } = posSplitPacks(total, size);
  const parts = [];
  if (packs > 0) parts.push(`${packs} ${posPackNoun(product, packs)}`);
  if (loose > 0 || packs === 0) parts.push(`${loose} ${posUnitNoun(product, loose)}`);
  return parts.join(' and ');
};

/**
 * Turns a packs-and-pieces entry into the one number everything else uses.
 *
 * @param {{packs?: number|string, units?: number|string}} amount
 * @param {number} packSize
 * @returns {number} total units, 0 when nothing usable was entered
 */
export const posUnitsFromEntry = (amount, packSize) => {
  const packs = wholeUnits(amount?.packs);
  const loose = wholeUnits(amount?.units);
  return (packs * wholeUnits(packSize)) + loose;
};

/**
 * Records a delivery into the storeroom.
 *
 * A DELTA, never a new total — the same reasoning as addPosStock. "Two boxes
 * arrived" is a claim the person holding the delivery note can make correctly;
 * "there are now nine boxes" silently depends on nobody having refilled the
 * fridge since they last looked.
 *
 * Applied as a transaction against reserveUnits alone, so it cannot disturb
 * the active count beside it.
 *
 * @param {string} clientId
 * @param {object} product - with `id`, `name` and the pack size
 * @param {{packs?: number|string, units?: number|string}} amount - what arrived
 * @param {string} [actor]
 * @returns {Promise<number>} the reserve after the delivery, in units
 * @throws {Error} user-facing, when nothing usable was entered
 */
export const addPosReserve = async (clientId, product, amount, actor = '') => {
  if (!product?.id) throw userFacingError('Save the product before adding reserve stock to it.');

  const packSize = posPackSize(product);
  const added = posUnitsFromEntry(amount, packSize);
  if (added < 1) {
    throw userFacingError('Enter how many arrived — a number of packs, a number of pieces, or both.');
  }
  if (added > POS_RESERVE_UNITS_MAX) {
    throw userFacingError(`That is more than this till can record at once (${POS_RESERVE_UNITS_MAX}).`);
  }

  const reserveRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/reserveUnits`);
  const result = await runTransaction(reserveRef, (current) => wholeUnits(current) + added);
  if (!result.committed) throw userFacingError('Could not add reserve stock just now. Please try again.');

  const after = wholeUnits(result.snapshot.val());
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.RESERVE_ADDED,
    entityId: product.id,
    summary: {
      name: product.name,
      added,
      addedText: describePosUnits(added, product),
      before: after - added,
      after,
      afterText: describePosUnits(after, product),
    },
  });
  return after;
};

/**
 * Corrects the reserve to an exact figure — a storeroom stocktake.
 *
 * @param {string} clientId
 * @param {object} product
 * @param {number|string} units - the count that is actually there
 * @param {string} [actor]
 * @returns {Promise<number>} the reserve after the correction
 * @throws {Error} user-facing, on anything that is not a whole count
 */
export const setPosReserve = async (clientId, product, units, actor = '') => {
  if (!product?.id) throw userFacingError('Save the product before correcting its reserve stock.');

  // EMPTY IS NOT ZERO, for the same reason it is not in setPosStock: Number('')
  // is 0, and a cleared field would wipe the storeroom without anyone meaning
  // it. A value has to be present before it is converted.
  const provided = typeof units === 'number'
    || (typeof units === 'string' && units.trim() !== '');
  const next = provided ? Number(units) : NaN;
  if (!Number.isInteger(next) || next < 0 || next > POS_RESERVE_UNITS_MAX) {
    throw userFacingError('Enter the reserve count as a whole number, 0 or more.');
  }

  const reserveRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}/reserveUnits`);
  let before = 0;
  const result = await runTransaction(reserveRef, (current) => {
    before = wholeUnits(current);
    return next;
  });
  if (!result.committed) throw userFacingError('Could not update the reserve just now. Please try again.');

  const after = wholeUnits(result.snapshot.val());
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.RESERVE_ADJUSTED,
    entityId: product.id,
    summary: {
      name: product.name,
      before,
      after,
      change: after - before,
      afterText: describePosUnits(after, product),
    },
  });
  return after;
};

/**
 * Carries stock out of the storeroom and onto the shelf.
 *
 * ONE TRANSACTION ON THE PRODUCT, not two on its fields. Nothing is created or
 * destroyed here — the same units leave one count and join the other — so the
 * two changes have to commit together or not at all. Two separate writes have
 * a moment in between where the stock exists in neither place (or in both),
 * and the one that fails is the one that leaves the books wrong.
 *
 * The updater returns the whole record with two numbers changed, so any field
 * edited elsewhere in the meantime passes through untouched and the
 * transaction simply retries if it moved.
 *
 * PARTIAL PACKS ARE NORMAL. "Ten bottles, not a whole box" is the ordinary
 * case at a counter, so packs and loose pieces are added together into one
 * figure rather than the caller having to open a box first.
 *
 * @param {string} clientId
 * @param {object} product - with `id`, `name` and the pack size
 * @param {{packs?: number|string, units?: number|string}} amount - what to move
 * @param {string} [actor]
 * @returns {Promise<{moved: number, active: number, reserve: number}>} the counts after
 * @throws {Error} user-facing, when nothing was asked for or the reserve is short
 */
export const replenishActiveFromReserve = async (clientId, product, amount, actor = '') => {
  if (!product?.id) throw userFacingError('Save the product before moving its stock.');

  const packSize = posPackSize(product);
  const moving = posUnitsFromEntry(amount, packSize);
  if (moving < 1) {
    throw userFacingError('Enter how much to move — a number of packs, a number of pieces, or both.');
  }

  const productRef = tenantRef(clientId, `${POS_PRODUCTS_NODE}/${product.id}`);
  // Captured inside the updater rather than read beforehand: these are the
  // values the write actually replaced, which are the only ones worth
  // reporting on the trail.
  let before = { active: 0, reserve: 0 };
  let shortOf = null;

  const result = await runTransaction(productRef, (current) => {
    // undefined ABORTS. It is the only way to refuse from inside a
    // transaction without writing something wrong.
    if (!current) return undefined;
    const reserve = wholeUnits(current.reserveUnits);
    if (reserve < moving) {
      shortOf = reserve;
      return undefined;
    }
    const active = wholeUnits(current.supplies);
    before = { active, reserve };
    return { ...current, reserveUnits: reserve - moving, supplies: active + moving };
  });

  if (!result.committed) {
    // Told apart, because they need different things from the person reading
    // it: one is "you asked for more than is there", the other is "try again".
    if (shortOf !== null) {
      throw userFacingError(
        `Only ${describePosUnits(shortOf, product)} left in reserve — that is less than you asked for.`,
      );
    }
    throw userFacingError('Could not move that stock just now. Please try again.');
  }

  const saved = result.snapshot.val() || {};
  const after = { active: wholeUnits(saved.supplies), reserve: wholeUnits(saved.reserveUnits) };

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.STOCK_REPLENISHED,
    entityId: product.id,
    summary: {
      name: product.name,
      moved: moving,
      movedText: describePosUnits(moving, product),
      activeBefore: before.active,
      activeAfter: after.active,
      reserveBefore: before.reserve,
      reserveAfter: after.reserve,
      reserveAfterText: describePosUnits(after.reserve, product),
    },
  });

  return { moved: moving, active: after.active, reserve: after.reserve };
};

// ---- Sorting the product grid ---------------------------------------------

/**
 * Compares two strings the way a person reads a list: case-insensitively, and
 * with numbers inside them in numeric order, so "Water 2L" follows "Water 500ml"
 * rather than preceding it on the strength of its first digit.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true,
});

/**
 * Orders the product grid.
 *
 * RETURNS A NEW ARRAY. The list it is given comes straight from React state,
 * and Array.prototype.sort works in place — sorting it where it lies would
 * mutate state directly and leave the grid re-rendering from a list React
 * believes it has already drawn.
 *
 * NAME IS THE TIE-BREAK for every field, and it always runs A-Z even when the
 * primary sort is descending. Two products at the same price, or with the same
 * stock, have no meaningful order between them — leaving that to the database's
 * key order makes the grid reshuffle for no visible reason, and reversing the
 * tie-break as well would scramble each group's internal order every time the
 * direction button is pressed.
 *
 * PRODUCTS WITH NO CATEGORY SORT LAST in both directions. "Unfiled" is not a
 * name that falls before A or after Z; it is the absence of one, and it belongs
 * at the end of the list either way.
 *
 * @param {object[]} products - the products to order; never modified
 * @param {object} [options]
 * @param {string} [options.key] - one of POS_PRODUCT_SORT_KEYS; anything else
 *   falls back to the default, so a stale stored choice cannot blank the grid
 * @param {string} [options.direction] - POS_SORT_ASC or POS_SORT_DESC
 * @param {Map<string, number>} [options.unitsSold] - units sold per product id,
 *   from tallyPosUnitsSold. Absent means nothing has sold, which is also what
 *   an empty Map means, so the 'sold' order is stable while orders load.
 * @param {string} [options.scope='active'] - WHICH STOCK the 'stock' order
 *   reads. Inventory shows the shelf and the storeroom through the same list,
 *   and "lowest stock" on the Reserve tab has to mean the emptiest storeroom —
 *   ordering that tab by a count it is not displaying is a list that looks
 *   shuffled.
 * @param {string[]} [options.customOrder] - product ids in the hand-arranged
 *   order, for the 'custom' key. Products missing from it (registered after
 *   the order was saved) follow the arranged ones, A-Z. Direction is ignored:
 *   an arrangement has no "reverse" anyone asked for.
 * @returns {object[]} a new, ordered array
 */
export const sortPosProducts = (products, options = {}) => {
  const list = [...(products || [])];
  const key = POS_PRODUCT_SORT_KEYS.includes(options.key) ? options.key : POS_PRODUCT_SORT_DEFAULT;
  const sign = options.direction === POS_SORT_DESC ? -1 : 1;
  const unitsSold = options.unitsSold;

  /** This product's units sold, 0 for one that has never appeared on an order. */
  const sold = (product) => Number(unitsSold?.get(product?.id)) || 0;
  /** The stock this list is ABOUT — the shelf, or the storeroom behind it. */
  const stock = options.scope === 'reserve' ? posReserveUnits : posActiveUnits;

  const byName = (a, b) => compareText(a?.name, b?.name);

  if (key === POS_SORT_BY_CUSTOM) {
    const position = new Map((options.customOrder || []).map((id, index) => [id, index]));
    return list.sort((a, b) => {
      const pa = position.has(a?.id) ? position.get(a.id) : Infinity;
      const pb = position.has(b?.id) ? position.get(b.id) : Infinity;
      if (pa !== pb) return pa < pb ? -1 : 1;
      return byName(a, b);
    });
  }

  const comparators = {
    name: (a, b) => sign * byName(a, b),
    category: (a, b) => {
      const ca = String(a?.category || '').trim();
      const cb = String(b?.category || '').trim();
      // Unfiled last, before the direction is applied — hence the early return
      // rather than folding it into the comparison below.
      if (!ca !== !cb) return ca ? -1 : 1;
      const byCategory = compareText(ca, cb);
      return byCategory !== 0 ? sign * byCategory : byName(a, b);
    },
    price: (a, b) => {
      const diff = (Number(a?.price) || 0) - (Number(b?.price) || 0);
      return diff !== 0 ? sign * diff : byName(a, b);
    },
    sold: (a, b) => {
      const diff = sold(a) - sold(b);
      return diff !== 0 ? sign * diff : byName(a, b);
    },
    stock: (a, b) => {
      const diff = stock(a) - stock(b);
      return diff !== 0 ? sign * diff : byName(a, b);
    },
  };

  return list.sort(comparators[key]);
};

// ---- Staff role rights, as the club sets them ------------------------------
//
// The administrator defines each role's DEFAULT rights on the client record
// (posClients/<id>/roles). A store admin may change what a role allows at
// THEIR club, which is stored here as an override keyed by role id. The
// default is never touched, so "Reset to default" is just deleting the entry.

/**
 * Live map of this club's role overrides: { [roleId]: string[] permissions }.
 *
 * @param {string} clientId
 * @param {(overrides: Object<string, string[]>) => void} callback
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribePosRoleOverrides = (clientId, callback, onError) => onValue(
  tenantRef(clientId, POS_ROLE_OVERRIDES_NODE),
  (snapshot) => {
    const out = {};
    Object.entries(snapshot.val() || {}).forEach(([roleId, value]) => {
      out[roleId] = Object.keys(value?.permissions || {}).filter((key) => POS_PERMISSION_KEYS.includes(key));
    });
    callback(out);
  },
  (error) => {
    console.error('Role overrides listener failed:', error);
    if (onError) onError(error);
  },
);

/**
 * The ids of roles this club has customised, read once — for the admin's
 * Roles tab, which only needs to know which ones to flag.
 *
 * @param {string} clientId
 * @returns {Promise<Set<string>>}
 */
export const fetchPosRoleOverrideIds = async (clientId) => {
  const snapshot = await get(tenantRef(clientId, POS_ROLE_OVERRIDES_NODE));
  return new Set(Object.keys(snapshot.val() || {}));
};

/**
 * Saves what one role may do at this club.
 *
 * @param {string} clientId
 * @param {{id: string, name: string, permissions: string[]}} role - the role
 *   with its DEFAULT permissions, for the trail's "before"
 * @param {string[]} permissions - the club's chosen rights
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const savePosRoleOverride = async (clientId, role, permissions, actor = '') => {
  if (!role?.id) throw userFacingError('That role has no id.');
  if (isStoreAdminRole(role)) throw userFacingError('The store admin role always has full access and cannot be changed here.');
  const clean = (permissions || []).filter((key) => POS_PERMISSION_KEYS.includes(key));

  await set(tenantRef(clientId, `${POS_ROLE_OVERRIDES_NODE}/${role.id}`), {
    name: role.name,
    permissions: clean.reduce((map, key) => ({ ...map, [key]: true }), {}),
    updatedAt: Date.now(),
    updatedBy: resolveActor(actor),
  });

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.ROLE_RIGHTS_UPDATED,
    entityId: role.id,
    summary: { role: role.name, granted: clean, default: role.permissions || [] },
  });
};

/**
 * Drops this club's override for a role, so the administrator's default
 * applies again.
 *
 * @param {string} clientId
 * @param {{id: string, name: string}} role
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const resetPosRoleOverride = async (clientId, role, actor = '') => {
  await remove(tenantRef(clientId, `${POS_ROLE_OVERRIDES_NODE}/${role.id}`));
  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.ROLE_RIGHTS_RESET,
    entityId: role.id,
    summary: { role: role.name, restored: role.permissions || [] },
  });
};
